import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  RefreshCw,
  Megaphone,
  TrendingUp,
  Users,
  DollarSign,
  Target,
  Award,
  Percent,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Sparkles,
  Mail,
  Phone,
  MapPin,
  Loader2,
  ExternalLink,
  Database,
  Info,
  CircleDot,
  Crown,
  ThumbsDown,
  Wand2,
  Calendar,
  ListTodo,
  Flame,
  Hourglass,
  X,
  TrendingDown,
  Languages,
  ShieldCheck,
  Wallet,
  Globe,
  Globe2,
  ClipboardList,
  MessageSquare,
  CalendarClock,
  MessagesSquare,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { crmUrl } from "../lib/crmUrl";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "../components/ui/tooltip";
import DealActivityTimeline from "../components/DealActivityTimeline";

/**
 * Marketing Analytics — JOIN of Meta Ads spend with CRM leads/deals.
 *
 * Single overview endpoint (`GET /api/marketing-analytics/overview`) feeds the
 * whole page. Date range defaults to last 30 days; user can override and refresh.
 *
 * Sections:
 *  - KPI cards row (spend, leads, CPL, won, ROAS, win rate, CAC)
 *  - Funnel (impressions → clicks → LPV → leads → open → won)
 *  - Campaign table (per utm_campaign metrics with computed CPL/CAC/ROAS)
 *  - Owner × campaign matrix (quick-read who closes which source)
 *  - Daily spend trend (minimal bars, scales to range)
 */

const ALLOWED_ROLES = new Set([
  "admin",
  "sales_director",
  "crm_manager",
  "sales",
  "pm_lead",
]);

function isoNDaysAgo(n) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

function isoToday() {
  return new Date().toISOString().slice(0, 10);
}

// Calendar-month helpers. anchorMonthsAgo(0) = current month; (1) = previous.
// Returns {from, to, label} where from = first day of the month, to = last day
// (clamped to today for the current month so we don't ship future dates).
//
// CRITICAL: label MUST be formatted in UTC. The Date objects are constructed
// in UTC (Date.UTC(2026, 3, 1) = April 1 UTC). toLocaleString without an
// explicit timeZone uses the browser's LOCAL zone, which for any TZ west of
// UTC shifts April-1-UTC back to March-31-local — and the label silently
// reads "Mar 2026" while the from/to pickers correctly show "04/01–04/30".
function calendarMonthRange(anchorMonthsAgo = 1) {
  const now = new Date();
  const target = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - anchorMonthsAgo, 1));
  const y = target.getUTCFullYear();
  const m = target.getUTCMonth();
  const first = new Date(Date.UTC(y, m, 1));
  let last = new Date(Date.UTC(y, m + 1, 0));
  if (anchorMonthsAgo === 0 && last > now) last = new Date(now);
  const fmtMonth = first.toLocaleString("en-GB", {
    month: "short", year: "numeric", timeZone: "UTC",
  });
  return {
    from: first.toISOString().slice(0, 10),
    to: last.toISOString().slice(0, 10),
    label: fmtMonth,
  };
}

// All-time = back to the start of mirrored CRM history. We use 2026-01-01 as
// floor — older data exists but mostly without UTM tagging.
function allTimeRange() {
  return { from: "2026-01-01", to: isoToday(), label: "All time" };
}

function fmtUsd(n, opts = {}) {
  const v = Number(n) || 0;
  const dec = opts.decimals ?? (Math.abs(v) >= 1000 ? 0 : 2);
  return `$${v.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
}

function fmtInt(n) {
  return (Number(n) || 0).toLocaleString();
}

function fmtPct(p) {
  return `${((Number(p) || 0) * 100).toFixed(1)}%`;
}

const STAGE_BADGES = {
  won: { label: "Won", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  lost: { label: "Lost", cls: "bg-red-500/20 text-red-300 border-red-500/30" },
  open: { label: "Open", cls: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
};

// Color tier per stage win-probability — gives the user a quick read on where
// in the funnel a deal sits without forcing them to recall CRM stage names.
function stageBadgeFor(statusClass, probability) {
  if (statusClass === "won") return STAGE_BADGES.won.cls;
  if (statusClass === "lost") return STAGE_BADGES.lost.cls;
  const p = Number(probability) || 0;
  if (p >= 0.9) return "bg-emerald-500/15 text-emerald-300 border-emerald-500/25";
  if (p >= 0.5) return "bg-cyan-500/15 text-cyan-300 border-cyan-500/25";
  if (p >= 0.3) return "bg-amber-500/15 text-amber-300 border-amber-500/25";
  return "bg-zinc-500/15 text-zinc-300 border-zinc-600/30";
}

// Quality tier visual + label maps. Match the backend _QUALITY_TIERS enum
// in marketing_analytics.py — change both together.
// UI chrome is English; the AI-generated summary inside the panel is Russian
// (that's what the LLM emits and what the user explicitly asked for).
const TIER_META = {
  premium: { label: "Premium", cls: "bg-emerald-500/25 text-emerald-200 border-emerald-400/40" },
  quality: { label: "Quality", cls: "bg-cyan-500/20 text-cyan-200 border-cyan-400/30" },
  average: { label: "Average", cls: "bg-zinc-500/20 text-zinc-300 border-zinc-500/30" },
  weak: { label: "Weak", cls: "bg-amber-500/20 text-amber-200 border-amber-400/30" },
  junk: { label: "Junk", cls: "bg-red-500/25 text-red-200 border-red-400/40" },
};

const INTENT_LABEL = {
  very_high: "Very high intent",
  high: "High intent",
  medium: "Medium intent",
  low: "Low intent",
  unclear: "Unclear intent",
};

const BUDGET_LABEL = {
  explicit: "Budget mentioned",
  implied: "Budget implied",
  none: "No budget signal",
};

const SIZE_LABEL = {
  enterprise: "Enterprise (>$50k)",
  large: "Large ($10–50k)",
  medium: "Medium ($3–10k)",
  small: "Small (<$3k)",
  unknown: "Size unknown",
};

const GEO_LABEL = {
  primary: "Primary market",
  secondary: "Secondary market",
  low: "Low-fit geo",
  unknown: "Geo unknown",
};

function tierClsFor(tier) {
  return TIER_META[tier]?.cls || TIER_META.average.cls;
}

function tierLabelFor(tier) {
  return TIER_META[tier]?.label || TIER_META.average.label;
}

function scoreColorClass(score) {
  const n = Number(score) || 0;
  if (n >= 85) return "text-emerald-300";
  if (n >= 65) return "text-cyan-300";
  if (n >= 45) return "text-zinc-300";
  if (n >= 25) return "text-amber-300";
  return "text-red-300";
}

// Color a 0-100 metric chip background by tier. Matches scoreColorClass tiers.
function scoreBgClass(score) {
  if (score == null) return "bg-zinc-800/40 border-zinc-700/50 text-zinc-500";
  const n = Number(score) || 0;
  if (n >= 85) return "bg-emerald-500/15 border-emerald-500/30 text-emerald-300";
  if (n >= 65) return "bg-cyan-500/15 border-cyan-500/30 text-cyan-300";
  if (n >= 45) return "bg-zinc-700/30 border-zinc-600/40 text-zinc-200";
  if (n >= 25) return "bg-amber-500/15 border-amber-500/30 text-amber-300";
  return "bg-red-500/15 border-red-500/30 text-red-300";
}

// v10 — Marketing Lead Quality grade visual meta.
// Excellent/Good = scale-able sources, emerald/cyan;
// Average = baseline, zinc;
// Weak/Bad = action needed, amber/red;
// Insufficient data = neutral grey.
const MARKETING_GRADE_META = {
  Excellent: { cls: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40", tone: "emerald" },
  Good:      { cls: "bg-cyan-500/15 text-cyan-200 border-cyan-500/40",          tone: "cyan" },
  Average:   { cls: "bg-zinc-700/40 text-zinc-200 border-zinc-600/50",          tone: "zinc" },
  Weak:      { cls: "bg-amber-500/15 text-amber-200 border-amber-500/40",       tone: "amber" },
  Bad:       { cls: "bg-red-500/15 text-red-200 border-red-500/40",             tone: "red" },
  "Insufficient data": { cls: "bg-zinc-800/40 text-zinc-400 border-zinc-700/50", tone: "zinc" },
};

// v10 — Sales Handling grade visual meta. Same palette + extra "Not applicable".
const SALES_GRADE_META = {
  Excellent: { cls: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40", tone: "emerald" },
  Good:      { cls: "bg-cyan-500/15 text-cyan-200 border-cyan-500/40",          tone: "cyan" },
  Average:   { cls: "bg-zinc-700/40 text-zinc-200 border-zinc-600/50",          tone: "zinc" },
  Weak:      { cls: "bg-amber-500/15 text-amber-200 border-amber-500/40",       tone: "amber" },
  Bad:       { cls: "bg-red-500/15 text-red-200 border-red-500/40",             tone: "red" },
  "Not applicable": { cls: "bg-zinc-800/40 text-zinc-400 border-zinc-700/50",    tone: "zinc" },
};

// v10 — Marketing suggested action chips. Each gets an icon + tone.
const MARKETING_ACTION_META = {
  "scale":            { label: "Scale",            Icon: TrendingUp,   cls: "bg-emerald-500/15 text-emerald-200 border-emerald-500/40" },
  "keep testing":     { label: "Keep testing",     Icon: Sparkles,     cls: "bg-cyan-500/15 text-cyan-200 border-cyan-500/40" },
  "fix targeting":    { label: "Fix targeting",    Icon: Target,       cls: "bg-amber-500/15 text-amber-200 border-amber-500/40" },
  "fix creative":     { label: "Fix creative",     Icon: Wand2,        cls: "bg-amber-500/15 text-amber-200 border-amber-500/40" },
  "pause":            { label: "Pause",            Icon: ThumbsDown,   cls: "bg-red-500/15 text-red-200 border-red-500/40" },
  "needs more data":  { label: "Needs more data",  Icon: Database,     cls: "bg-zinc-700/40 text-zinc-300 border-zinc-600/50" },
};

// v10 — Confidence chip meta. high=green border so reviewer can trust;
// low=grey + italic "informational only" feel.
const CONFIDENCE_META = {
  high:   { cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  medium: { cls: "bg-zinc-800/40 text-zinc-300 border-zinc-700" },
  low:    { cls: "bg-zinc-900/60 text-zinc-500 border-zinc-800 italic" },
};

// Canonical scorecard metric order + labels. Must match backend
// MANAGER_SCORECARD_METRICS in marketing_analytics.py.
const SCORECARD_METRICS = [
  { key: "response_speed",     short: "Speed",      label: "Response speed",     hint: "How fast manager replied to the lead's first signal" },
  { key: "opening",            short: "Opening",    label: "Opening",            hint: "How the conversation was opened — tone, intro, professionalism" },
  { key: "discovery",          short: "Discovery",  label: "Discovery",          hint: "Did manager surface needs — project type, scope, timeline, decision-maker?" },
  { key: "budget_talk",        short: "Budget",     label: "Budget conversation", hint: "Did manager raise budget and learn the number?" },
  { key: "presentation",       short: "Pres.",      label: "Presentation",       hint: "Quality of materials sent — relevance, cases vs generic deck" },
  { key: "objection_handling", short: "Objections", label: "Objection handling", hint: "How manager handled client doubts and pushback" },
  { key: "follow_up",          short: "Follow-up",  label: "Follow-up",          hint: "Cadence of touches — disappeared vs steady" },
  { key: "next_step_clarity",  short: "Next step",  label: "Next-step clarity",  hint: "Is there a clear next step — meeting set, demo booked, proposal due?" },
];

/**
 * Compact horizontal scorecard chip-row.
 * Renders 8 small N/100 chips, color-coded by score band.
 * Used in the leaderboard row AND inside expanded view.
 */
// Profile Skills 3-bucket layout — per-lead mirror of PeoplePage's per-manager
// Profile Skills view. Same 12 metrics in the same CRM / Calls / Meetings
// columns, same color coding, same progress bars. Aggregate avg per bucket
// at the top. Marker: lead-profile-skills-v1 (2026-05-27)
//
// Frontend mirror of backend _SKILL_BUCKETS in marketing_analytics.py:
//   CRM:      response_speed, follow_up, next_step_clarity        (scorecard)
//   Calls:    opening, discovery, budget_talk, objection_handling (scorecard)
//   Meetings: presentation                                         (scorecard)
//             + metrics, economic_buyer, identify_pain, champion   (meddic projected to 0-100)
// MEDDIC's covered=100 / partial=50 / missing=0 / na=null mapping matches
// _meddic_status_to_score in the backend so aggregates align exactly.
const LEAD_SKILL_BUCKETS = [
  {
    key: "crm", label: "CRM", iconKey: "Database",
    tooltip: "Process discipline — responsiveness, follow-up cadence, next-step hygiene.",
    metrics: [
      { src: "scorecard", key: "response_speed",    label: "Response speed", hint: "How fast manager replied to the lead's first signal" },
      { src: "scorecard", key: "follow_up",         label: "Follow-up",      hint: "Cadence of touches — disappeared vs steady" },
      { src: "scorecard", key: "next_step_clarity", label: "Next step",      hint: "Concrete next step with a date set?" },
    ],
  },
  {
    key: "calls", label: "Calls", iconKey: "Phone",
    tooltip: "What happens during a call — opening, discovery, objections, value.",
    metrics: [
      { src: "scorecard", key: "opening",            label: "Intro",       hint: "Opens call with structured intro" },
      { src: "scorecard", key: "discovery",          label: "Discovery",   hint: "Asks SPIN-grade discovery questions" },
      { src: "scorecard", key: "budget_talk",        label: "Budget talk", hint: "Surfaces a real budget number" },
      { src: "scorecard", key: "objection_handling", label: "Objections",  hint: "Addresses objections with a referenced response" },
    ],
  },
  {
    key: "meetings", label: "Meetings", iconKey: "Users",
    tooltip: "Higher-order qualification — MEDDIC coverage, materials, close commitment.",
    metrics: [
      { src: "scorecard", key: "presentation",   label: "Presentation",      hint: "Sends materials that match discovery" },
      { src: "meddic",    key: "metrics",        label: "Metrics qualified", hint: "Extracted measurable value (deal size, scope)" },
      { src: "meddic",    key: "economic_buyer", label: "Economic buyer",    hint: "Identified who pays / decision-maker" },
      { src: "meddic",    key: "identify_pain",  label: "Identify pain",     hint: "Uncovered explicit pain / trigger" },
      { src: "meddic",    key: "champion",       label: "Champion",          hint: "Has someone inside pushing for us" },
    ],
  },
];

function _meddicStatusToScore(status) {
  if (!status) return null;
  const s = String(status).toLowerCase();
  if (s === "covered") return 100;
  if (s === "partial") return 50;
  if (s === "missing") return 0;
  return null; // "na" = not applicable — excluded from average
}

function _resolveMetricScore(m, scorecard, meddic) {
  if (m.src === "scorecard") {
    const entry = (scorecard || {})[m.key];
    return { score: entry?.score ?? null, note: entry?.note || "", status: null };
  }
  const entry = (meddic || {})[m.key];
  const score = _meddicStatusToScore(entry?.status);
  return { score, note: entry?.note || "", status: entry?.status || null };
}

function _scoreToneClasses(score) {
  if (score == null) return { text: "text-zinc-600", bar: "bg-zinc-800 border border-dashed border-zinc-700" };
  if (score >= 70) return { text: "text-emerald-300", bar: "bg-emerald-400" };
  if (score >= 50) return { text: "text-amber-300",   bar: "bg-amber-400" };
  return { text: "text-red-300", bar: "bg-red-400" };
}

const LeadMetricRow = ({ m, scorecard, meddic }) => {
  const { score, note, status } = _resolveMetricScore(m, scorecard, meddic);
  const width = score != null ? `${Math.max(2, score)}%` : "0%";
  const cls = _scoreToneClasses(score);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help">
          <div className="flex items-center justify-between text-[11px]">
            <span className="text-zinc-200">{m.label}</span>
            <span className={`font-bold ${cls.text}`}>{score ?? "—"}</span>
          </div>
          <div className="mt-1 h-1.5 w-full bg-zinc-900 rounded overflow-hidden">
            <div className={`h-full ${cls.bar}`} style={{ width }} />
          </div>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[300px]">
        <div className="text-[11px] space-y-1">
          <div className="font-medium">{m.label} {score != null && <span className="text-zinc-400">— {score}/100</span>}</div>
          <div className="text-zinc-400">{m.hint}</div>
          {m.src === "meddic" && status && (
            <div className="text-zinc-300 italic">MEDDIC status: {status}</div>
          )}
          {note ? (
            <div className="text-zinc-100 leading-snug">{note}</div>
          ) : score == null ? (
            <div className="text-zinc-500 italic">AI didn't extract this metric — typically because there wasn't enough engagement data (call / meeting / message thread) to evaluate it for this lead.</div>
          ) : null}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

const LeadProfileSkills = ({ scorecard, meddic, iconMap }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
      {LEAD_SKILL_BUCKETS.map((b) => {
        const scores = b.metrics
          .map((m) => _resolveMetricScore(m, scorecard, meddic).score)
          .filter((s) => s != null);
        const avg = scores.length ? Math.round(scores.reduce((a, c) => a + c, 0) / scores.length) : null;
        const Icon = iconMap[b.iconKey];
        const avgCls = _scoreToneClasses(avg).text;
        return (
          <div key={b.key} className="bg-zinc-950/40 border border-zinc-800 rounded p-2.5 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-zinc-200 text-[11px] uppercase tracking-wider inline-flex items-center gap-1 cursor-help">
                    {Icon && <Icon className="w-3 h-3" />} {b.label}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs text-[11px]">{b.tooltip}</TooltipContent>
              </Tooltip>
              <span className={`text-lg font-bold ${avgCls}`}>{avg ?? "—"}</span>
            </div>
            <div className="space-y-2 pt-1">
              {b.metrics.map((m) => (
                <LeadMetricRow key={`${m.src}:${m.key}`} m={m} scorecard={scorecard} meddic={meddic} />
              ))}
            </div>
            <div className="text-[9px] text-zinc-600 text-right pt-0.5">
              {scores.length}/{b.metrics.length} scored
            </div>
          </div>
        );
      })}
    </div>
  );
};


// Option B (2026-05-27) — always render all 8 scorecard chips. Missing
// metrics show "—" with a tooltip explaining WHY: either AI didn't see
// enough engagement data, or this lead was graded with a pre-v5 prompt.
// Marker: scorecard-always-12-v1 (legacy — replaced by LeadProfileSkills above)
const ScorecardChips = ({ scorecard, size = "sm" }) => {
  const sc = scorecard || {};
  const px = size === "lg" ? "px-2 py-1" : "px-1.5 py-0.5";
  const txt = size === "lg" ? "text-[11px]" : "text-[10px]";
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {SCORECARD_METRICS.map((m) => {
        const entry = sc[m.key];
        const score = entry?.score;
        const note = entry?.note;
        const isMissing = score == null;
        return (
          <Tooltip key={m.key}>
            <TooltipTrigger asChild>
              <span
                className={`inline-flex items-center gap-1 ${px} ${txt} rounded border whitespace-nowrap ${
                  isMissing
                    ? "bg-zinc-900/40 text-zinc-600 border-zinc-800 border-dashed"
                    : scoreBgClass(score)
                }`}
              >
                <span className="opacity-80">{m.short}</span>
                <span className="font-semibold">{isMissing ? "—" : score}</span>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-[280px] text-[11px]">
              <div className="font-medium">
                {m.label} — {isMissing ? <span className="text-zinc-400">not scored</span> : `${score}/100`}
              </div>
              <div className="text-zinc-300 text-[10px] mt-0.5">{m.hint}</div>
              {isMissing ? (
                <div className="text-zinc-400 mt-1 leading-snug text-[10px] italic">
                  AI didn't extract a score for this metric — most often because there's not enough engagement data (no calls / meetings / messages where this would surface). Re-run "Regenerate" if engagement was added recently.
                </div>
              ) : note ? (
                <div className="text-zinc-100 mt-1 leading-snug">{note}</div>
              ) : null}
            </TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
};

/**
 * Dialogue analysis block — what client asked, what manager replied, what
 * manager missed, and strengths/weaknesses. Shown inside expanded AI panel.
 */
const DialogueAnalysisBlock = ({ analysis }) => {
  if (!analysis) return null;
  const hasAny =
    (analysis.client_questions || []).length +
    (analysis.client_concerns || []).length +
    (analysis.manager_responses || []).length +
    (analysis.missed_topics || []).length +
    (analysis.strengths || []).length +
    (analysis.weaknesses || []).length > 0;
  if (!hasAny) return null;

  const Section = ({ title, items, color = "zinc", icon = null }) => {
    if (!items || items.length === 0) return null;
    const cls = {
      zinc: "border-zinc-700/40 bg-zinc-800/30",
      cyan: "border-cyan-500/25 bg-cyan-500/5",
      orange: "border-orange-500/25 bg-orange-500/5",
      amber: "border-amber-500/25 bg-amber-500/5",
      emerald: "border-emerald-500/25 bg-emerald-500/5",
      red: "border-red-500/25 bg-red-500/5",
    }[color];
    const titleCls = {
      zinc: "text-zinc-400",
      cyan: "text-cyan-300",
      orange: "text-orange-300",
      amber: "text-amber-300",
      emerald: "text-emerald-300",
      red: "text-red-300",
    }[color];
    return (
      <div className={`border rounded px-2 py-1.5 ${cls}`}>
        <div className={`text-[10px] uppercase tracking-wider mb-1 inline-flex items-center gap-1 ${titleCls}`}>
          {icon}
          {title}
        </div>
        <ul className="space-y-0.5 text-[11px] text-zinc-200">
          {items.map((x, i) => (
            <li key={i} className="leading-snug">• {x}</li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-wider text-blue-300 inline-flex items-center gap-1">
        <Sparkles className="w-3 h-3" /> Dialogue analysis
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <Section title="Client asked / wants" items={analysis.client_questions} color="cyan" />
        <Section title="Client concerns / objections" items={analysis.client_concerns} color="amber" />
        <Section title="Manager responses" items={analysis.manager_responses} color="zinc" />
        <Section title="Manager missed / didn't ask" items={analysis.missed_topics} color="orange" />
        <Section title="Manager strengths" items={analysis.strengths} color="emerald" />
        <Section title="Manager weaknesses" items={analysis.weaknesses} color="red" />
      </div>
    </div>
  );
};

function relTime(iso) {
  if (!iso) return "never";
  try {
    const t = new Date(iso).getTime();
    if (!t) return "never";
    const diffSec = Math.max(0, Math.round((Date.now() - t) / 1000));
    if (diffSec < 60) return `${diffSec}s ago`;
    if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`;
    if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`;
    return `${Math.round(diffSec / 86400)}d ago`;
  } catch (_) {
    return "never";
  }
}

// ============================ v7 COACHING BLOCKS ============================
// MEDDIC qualification framework — visual status grid (covered/partial/missing).
const MEDDIC_META = [
  { key: "metrics",            label: "Metrics",            hint: "Measurable value for the client — what gets better with our work" },
  { key: "economic_buyer",     label: "Economic buyer",     hint: "Who actually pays / signs — name and role" },
  { key: "decision_criteria",  label: "Decision criteria",  hint: "What matters most to them — price/portfolio/timeline/trust" },
  { key: "decision_process",   label: "Decision process",   hint: "How they decide — one CEO / board / 3 vendor bids" },
  { key: "identify_pain",      label: "Identify pain",      hint: "Why they need this NOW — the trigger" },
  { key: "champion",           label: "Champion",           hint: "Who inside is pushing for us" },
];
// Option B (2026-05-27) — distinguish "missing" (AI evaluated, said it's
// not covered) from "not_extracted" (AI didn't include the field at all).
// `missing` = real signal ("manager didn't surface the economic buyer").
// `not_extracted` = sparse-data fallback ("AI couldn't tell either way").
const MEDDIC_STATUS_META = {
  covered:       { label: "Covered",       cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" },
  partial:       { label: "Partial",       cls: "bg-amber-500/15 border-amber-500/40 text-amber-300" },
  missing:       { label: "Missing",       cls: "bg-red-500/15 border-red-500/40 text-red-300" },
  na:            { label: "N/A",           cls: "bg-zinc-700/30 border-zinc-600/40 text-zinc-400" },
  not_extracted: { label: "Not analyzed",  cls: "bg-zinc-900/40 border-zinc-800 text-zinc-600 border-dashed" },
};

const MeddicBlock = ({ meddic }) => {
  const md = meddic || {};
  return (
    <div className="bg-zinc-950/40 border border-zinc-800 rounded px-2 py-2 space-y-1.5">
      <div className="text-zinc-300 text-[10px] uppercase tracking-wider inline-flex items-center gap-1">
        <ShieldCheck className="w-3 h-3" /> MEDDIC qualification
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
        {MEDDIC_META.map((m) => {
          const entry = md[m.key];
          // Field genuinely absent from AI output → "not_extracted" (dashed gray).
          // Field present but empty / "missing" → "missing" (red — manager dropped the ball).
          const hasEntry = entry && (entry.status || entry.note);
          const status = hasEntry ? (entry.status || "missing") : "not_extracted";
          const meta = MEDDIC_STATUS_META[status] || MEDDIC_STATUS_META.not_extracted;
          return (
            <div key={m.key} className="bg-zinc-900/50 border border-zinc-800 rounded px-2 py-1.5">
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-zinc-200 text-[11px] font-medium cursor-help">{m.label}</span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-[11px] max-w-[260px]">{m.hint}</TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className={`inline-flex items-center px-1.5 py-0.5 text-[9px] rounded border cursor-help ${meta.cls}`}>
                      {meta.label}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-[10px] max-w-[260px]">
                    {status === "not_extracted"
                      ? "AI didn't include this field — usually because engagement data was too thin to qualify."
                      : status === "missing"
                        ? "AI evaluated and said this is NOT covered — manager didn't surface it during dialogue."
                        : status === "partial"
                          ? "Partial — manager touched it but didn't fully qualify."
                          : status === "covered"
                            ? "Covered — manager explicitly surfaced this."
                            : "Not applicable to this lead."}
                  </TooltipContent>
                </Tooltip>
              </div>
              {entry?.note && <div className="text-zinc-400 text-[11px] leading-snug">{entry.note}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Phase-by-phase breakdown (rapport → discovery → presentation → objection → commitment).
const PHASE_META = [
  { key: "rapport",            label: "Rapport / opening" },
  { key: "discovery",          label: "Discovery (SPIN)" },
  { key: "presentation",       label: "Presentation" },
  { key: "objection_handling", label: "Objection handling" },
  { key: "commitment",         label: "Commitment / next step" },
];

const PhaseAnalysisBlock = ({ phases }) => {
  if (!phases) return null;
  return (
    <div className="bg-zinc-950/40 border border-zinc-800 rounded px-2 py-2 space-y-1.5">
      <div className="text-zinc-300 text-[10px] uppercase tracking-wider inline-flex items-center gap-1">
        <Target className="w-3 h-3" /> Phase-by-phase coach review
      </div>
      <div className="space-y-1.5">
        {PHASE_META.map((p) => {
          const entry = phases[p.key] || {};
          const score = entry.score;
          const wwd = entry.what_was_done;
          const note = entry.coach_note;
          if (score == null && !wwd && !note) return null;
          return (
            <div key={p.key} className="bg-zinc-900/50 border border-zinc-800 rounded px-2 py-1.5">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-zinc-200 text-[11px] font-medium">{p.label}</span>
                {score != null && (
                  <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] rounded border font-semibold ${scoreBgClass(score)}`}>
                    {score}/100
                  </span>
                )}
              </div>
              {wwd && (
                <div className="text-zinc-300 text-[11px] leading-snug mb-1">
                  <span className="text-zinc-500 mr-1 text-[10px] uppercase tracking-wider">What was done:</span>
                  {wwd}
                </div>
              )}
              {note && (
                <div className="text-violet-200 text-[11px] leading-snug">
                  <span className="text-violet-400 mr-1 text-[10px] uppercase tracking-wider">Coach:</span>
                  {note}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Ready-to-use script for the next call — opening / questions / objection / closing.
const NextCallScriptBlock = ({ script }) => {
  if (!script) return null;
  const questions = script.key_questions || [];
  const objections = script.anticipated_objections || [];
  return (
    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-2 space-y-2">
      <div className="text-emerald-300 text-[10px] uppercase tracking-wider inline-flex items-center gap-1">
        <Wand2 className="w-3 h-3" /> Next-call script (use verbatim)
      </div>
      {script.goal && (
        <div className="text-[12px]">
          <span className="text-emerald-400/80 text-[10px] uppercase tracking-wider mr-1">Goal:</span>
          <span className="text-emerald-100">{script.goal}</span>
        </div>
      )}
      {script.opening && (
        <div className="bg-zinc-950/40 border border-zinc-800 rounded p-2 text-[12px]">
          <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-0.5">Opening</div>
          <div className="text-zinc-100 leading-snug italic">«{script.opening}»</div>
        </div>
      )}
      {questions.length > 0 && (
        <div className="bg-zinc-950/40 border border-zinc-800 rounded p-2">
          <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Key questions (in order)</div>
          <ol className="space-y-1 text-[12px] text-zinc-200 list-decimal list-inside">
            {questions.map((q, i) => <li key={i} className="leading-snug">{q}</li>)}
          </ol>
        </div>
      )}
      {objections.length > 0 && (
        <div className="bg-zinc-950/40 border border-zinc-800 rounded p-2">
          <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-1">Anticipated objections + your response</div>
          <div className="space-y-1.5">
            {objections.map((o, i) => (
              <div key={i} className="text-[11px] leading-snug">
                <div className="text-amber-300">
                  <span className="text-amber-500/80 mr-1">If:</span>
                  «{o.objection}»
                </div>
                <div className="text-emerald-200 ml-3">
                  <span className="text-emerald-400/80 mr-1">→</span>
                  «{o.response}»
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {script.closing && (
        <div className="bg-zinc-950/40 border border-zinc-800 rounded p-2 text-[12px]">
          <div className="text-zinc-500 text-[10px] uppercase tracking-wider mb-0.5">Closing</div>
          <div className="text-zinc-100 leading-snug italic">«{script.closing}»</div>
        </div>
      )}
    </div>
  );
};

/**
 * Renders the AI quality analysis for a single lead. v2 cache rows carry
 * quality_score / quality_tier / signals / recommendation; v1 cache rows only
 * have plain text. The component handles both shapes and falls back to a
 * "click Generate" prompt when no summary exists yet.
 */
const AiQualityPanel = ({ summary, dealId, isSummarizing, onGenerate, coachView = "manager" }) => {
  const hasV2 = summary && (summary.quality_score != null || summary.quality_tier);
  const score = summary?.quality_score;
  const tier = summary?.quality_tier;
  const tierCls = tier ? tierClsFor(tier) : "bg-zinc-700/30 text-zinc-400 border-zinc-600/30";
  const tierLabel = tier ? tierLabelFor(tier) : null;
  // Lead-quality view hides manager-coaching sections (MEDDIC, phase analysis,
  // coaching feedback, next-call script, manager scorecard, alternative play).
  // Marketing reviewers only care about lead signals, not how the manager worked.
  const showManagerSections = coachView !== "lead_quality";

  return (
    <div className="bg-blue-500/5 border border-blue-500/20 rounded p-3 space-y-2">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-blue-300 text-[11px] uppercase tracking-wider">
          <Sparkles className="w-3 h-3" />
          AI quality analysis
        </span>
        <div className="flex items-center gap-2">
          {hasV2 && score != null && (
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded border ${tierCls}`}
              title="Lead quality score 0–100 with tier"
            >
              {tier === "premium" && <Crown className="w-3 h-3" />}
              {tier === "junk" && <ThumbsDown className="w-3 h-3" />}
              <span className="font-semibold">{score}</span>
              <span className="opacity-80">·</span>
              <span>{tierLabel}</span>
            </span>
          )}
          {dealId && (
            <button
              type="button"
              onClick={() => onGenerate(dealId, !!summary)}
              disabled={isSummarizing}
              className="text-[10px] text-blue-400 hover:text-blue-300 disabled:opacity-50 inline-flex items-center gap-1"
            >
              {isSummarizing ? (
                <><Loader2 className="w-3 h-3 animate-spin" /> Analyzing…</>
              ) : summary ? (
                "Regenerate"
              ) : (
                <><Wand2 className="w-3 h-3" /> Analyze</>
              )}
            </button>
          )}
        </div>
      </div>

      {hasV2 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {summary.intent_strength && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border bg-zinc-800/40 border-zinc-700 text-zinc-300">
              <Target className="w-2.5 h-2.5" /> {INTENT_LABEL[summary.intent_strength] || summary.intent_strength}
            </span>
          )}
          {summary.budget_signal && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border bg-zinc-800/40 border-zinc-700 text-zinc-300">
              <Wallet className="w-2.5 h-2.5" /> {BUDGET_LABEL[summary.budget_signal] || summary.budget_signal}
            </span>
          )}
          {summary.deal_size_estimate && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border bg-zinc-800/40 border-zinc-700 text-zinc-300">
              <DollarSign className="w-2.5 h-2.5" /> {SIZE_LABEL[summary.deal_size_estimate] || summary.deal_size_estimate}
            </span>
          )}
          {summary.geo_fit && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border bg-zinc-800/40 border-zinc-700 text-zinc-300">
              <Globe className="w-2.5 h-2.5" /> {GEO_LABEL[summary.geo_fit] || summary.geo_fit}
            </span>
          )}
        </div>
      )}

      {/* Marketing axis reasoning — one-line "why this lead looks the way it does"
          (was a full card; removed 2026-05-27 because top signal chips already
          render the conclusion and Manager Performance covers the sales axis).
          Surface only the reasoning + a collapsible signals_used list so the
          marketing rationale isn't lost entirely. Marker: collapse-axis-cards-v1 */}
      {(summary?.marketing?.reasoning || summary?.marketing?.signals_used?.length || summary?.sales?.bottleneck) && (
        <div className="space-y-1">
          {summary?.marketing?.reasoning && (
            <div className="text-zinc-300 leading-snug text-[12px] inline-flex items-start gap-1.5">
              <Megaphone className="w-3 h-3 text-purple-400 flex-shrink-0 mt-0.5" />
              <span><span className="text-purple-300 mr-1">Marketing:</span>{summary.marketing.reasoning}</span>
            </div>
          )}
          {summary?.sales?.bottleneck
            && summary.sales.bottleneck.toLowerCase() !== "no bottleneck"
            && summary.sales.bottleneck.toLowerCase() !== "not applicable" && (
            <div className="text-red-200 leading-snug text-[12px] inline-flex items-start gap-1.5">
              <Users className="w-3 h-3 text-amber-400 flex-shrink-0 mt-0.5" />
              <span><span className="text-amber-300 mr-1">Bottleneck:</span>{summary.sales.bottleneck}</span>
            </div>
          )}
          {summary?.marketing?.signals_used?.length > 0 && (
            <details className="text-[10px] text-zinc-500">
              <summary className="cursor-pointer hover:text-zinc-300 inline-flex items-center gap-1">
                Marketing signals used ({summary.marketing.signals_used.length})
              </summary>
              <ul className="mt-1 space-y-0.5 ml-4 list-disc">
                {summary.marketing.signals_used.map((s, i) => (
                  <li key={i} className="text-zinc-400">{s}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Disqualification reason — most important info for junk/weak leads.
          Surface FIRST so user reads the "why" before any prose. */}
      {summary?.disqualification_reason && (
        <div className="bg-zinc-900/60 border border-zinc-800 border-l-2 border-l-red-500/60 rounded px-3 py-2 text-[12px]">
          <div className="text-red-300 text-[10px] uppercase tracking-wider mb-0.5 inline-flex items-center gap-1">
            <ThumbsDown className="w-3 h-3" /> Why not buying
          </div>
          <div className="text-red-100 leading-snug font-medium">{summary.disqualification_reason}</div>
        </div>
      )}

      {/* Live activity timeline — pretty rendering of calls/notes/meetings/tasks
          from the CRM (stage history + call gradings; see the component note on
          Anar's notes are in Turkish). Replaces the raw <pre> dump that was here.
          Marker: deal-activity-timeline-v1 */}
      {dealId ? (
        <DealActivityTimeline dealId={dealId} compact defaultLang="ru" />
      ) : summary?.raw_activity_timeline ? (
        <details className="bg-zinc-950/40 border border-zinc-800 rounded px-2 py-1.5">
          <summary className="cursor-pointer text-zinc-400 text-[10px] uppercase tracking-wider inline-flex items-center gap-1 hover:text-zinc-200">
            <Database className="w-3 h-3" /> Raw activity timeline · {summary.raw_activity_timeline.length.toLocaleString()} chars (no deal_id — fallback view)
          </summary>
          <pre className="mt-2 whitespace-pre-wrap font-mono text-[11px] leading-snug text-zinc-300 max-h-[480px] overflow-y-auto">
            {summary.raw_activity_timeline}
          </pre>
        </details>
      ) : null}

      {/* Key facts — dated bullets extracted by AI from timeline. The
          coaching-grade evidence: dates, response times, what happened when.
          Surface BEFORE the prose summary so reviewer reads facts first. */}
      {summary?.key_facts && summary.key_facts.length > 0 && (
        <div className="bg-zinc-950/40 border border-zinc-800 rounded px-2 py-1.5 text-[12px]">
          <div className="text-zinc-400 text-[10px] uppercase tracking-wider mb-1 inline-flex items-center gap-1">
            <ListTodo className="w-3 h-3" /> Key facts (timeline)
          </div>
          <ul className="space-y-0.5">
            {summary.key_facts.map((f, i) => (
              <li key={i} className="text-zinc-200 leading-snug flex items-start gap-1.5">
                <span className="text-zinc-600 shrink-0">•</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Summary text (Russian) — short prose after facts */}
      {summary?.summary_ru || summary?.text ? (
        <div className="text-zinc-200 leading-relaxed whitespace-pre-wrap text-[12px]">
          {summary.summary_ru || summary.text}
        </div>
      ) : (
        <div className="text-zinc-500 italic">
          {dealId
            ? "Click Analyze to generate a structured Russian quality review (score, tier, signals, recommended next action)."
            : "No deal attached to this contact yet — nothing to analyze."}
        </div>
      )}

      {/* Engagement summary chip — what manager activity was used as input */}
      {summary?.engagement_counts && (
        (summary.engagement_counts.calls || summary.engagement_counts.notes
          || summary.engagement_counts.meetings || summary.engagement_counts.tasks) ? (
          <div className="flex items-center gap-2 flex-wrap text-[10px] text-zinc-500">
            <span className="uppercase tracking-wider">Based on manager activity:</span>
            {summary.engagement_counts.calls > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-zinc-800/40 border-zinc-700 text-zinc-300">
                <Phone className="w-2.5 h-2.5" /> {summary.engagement_counts.calls} call{summary.engagement_counts.calls === 1 ? "" : "s"}
              </span>
            )}
            {summary.engagement_counts.notes > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-zinc-800/40 border-zinc-700 text-zinc-300">
                <Mail className="w-2.5 h-2.5" /> {summary.engagement_counts.notes} note{summary.engagement_counts.notes === 1 ? "" : "s"}
              </span>
            )}
            {summary.engagement_counts.meetings > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-zinc-800/40 border-zinc-700 text-zinc-300">
                <Calendar className="w-2.5 h-2.5" /> {summary.engagement_counts.meetings} meeting{summary.engagement_counts.meetings === 1 ? "" : "s"}
              </span>
            )}
            {summary.engagement_counts.tasks > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border bg-zinc-800/40 border-zinc-700 text-zinc-300">
                <ListTodo className="w-2.5 h-2.5" /> {summary.engagement_counts.tasks} task{summary.engagement_counts.tasks === 1 ? "" : "s"}
              </span>
            )}
          </div>
        ) : (
          <div className="text-[10px] text-amber-400/80 italic">
            No manager activity available for this lead — the CRM does not expose activity-to-deal links, so the analysis uses landing-form text only.
          </div>
        )
      )}

      {/* Manager performance — v5: scorecard with 8 per-metric scores 0-100.
          Option B (2026-05-27, marker: scorecard-always-12-v1): we now always
          render this block when there's a summary at all, even if scorecard
          is empty. Missing chips show "—" with explanatory tooltip — so the
          modal aligns visually with PeoplePage's 12-metric Profile Skills.
          Hidden only in lead_quality view (marketing reviewer doesn't care). */}
      {showManagerSections && summary ? (() => {
        const sc = summary.manager_scorecard || {};
        const md = summary.meddic || {};
        // Count how many of the 8 scorecard metrics actually got a score, and
        // how many of the 6 MEDDIC fields got any status. If most are blank,
        // surface a helpful explainer so the user knows it's not a bug.
        const scoredCount = SCORECARD_METRICS.filter(m => sc[m.key]?.score != null).length;
        const meddicCount = MEDDIC_META.filter(m => md[m.key] && (md[m.key].status || md[m.key].note)).length;
        const totalSignals = SCORECARD_METRICS.length + MEDDIC_META.length;  // 14
        const coverage = scoredCount + meddicCount;
        const lowCoverage = coverage < 4;  // less than ~28% — likely FORM_LEAD with no engagement
        return (
          <div className="bg-zinc-900/60 border border-zinc-800 border-l-2 border-l-amber-500/60 rounded px-3 py-2 text-[12px] space-y-2">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-amber-300 text-[10px] uppercase tracking-wider inline-flex items-center gap-1">
                <Users className="w-3 h-3" /> Manager performance
                <span className="text-zinc-500 normal-case tracking-normal ml-1">
                  · {coverage}/{totalSignals} metrics extracted
                </span>
              </div>
              {summary?.overall_manager_score != null && (
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded border ${scoreBgClass(summary.overall_manager_score)}`}>
                  Overall <span className="font-semibold">{summary.overall_manager_score}/100</span>
                </span>
              )}
            </div>
            {lowCoverage && (
              <div className="bg-zinc-950/40 border border-zinc-800 border-l-2 border-l-amber-500/40 rounded px-2 py-1.5 text-[10px] text-zinc-400 leading-snug">
                <span className="text-amber-300">Low-coverage scorecard</span> — AI couldn't extract most metrics for this lead.
                Common cause: <strong className="text-zinc-300">FORM_LEAD without any engagement</strong> (no calls, meetings, or message threads to evaluate).
                Aggregate metrics on the <span className="text-zinc-300">People page</span> ignore these missing values, so they don't drag down a manager's average.
              </div>
            )}
            {/* 3-bucket Profile Skills layout — mirrors PeoplePage exactly.
                Per-lead view of the same 12 metrics aggregated per-manager
                on /marketing-analytics/people. Marker: lead-profile-skills-v1 */}
            <LeadProfileSkills
              scorecard={sc}
              meddic={md}
              iconMap={{ Database, Phone, Users }}
            />
            {summary?.manager_performance_note && (
              <div className="text-amber-100 leading-snug">{summary.manager_performance_note}</div>
            )}
          </div>
        );
      })() : null}

      {/* Dialogue analysis — what client asked / manager said / what was missed.
          Hidden in lead_quality view (it's about manager dialogue, not lead). */}
      {showManagerSections && summary?.dialogue_analysis && (
        <DialogueAnalysisBlock analysis={summary.dialogue_analysis} />
      )}

      {/* === v7 COACH-GRADE BLOCKS — all hidden in lead_quality view === */}

      {/* MEDDIC qualification — always render all 6 fields. Missing fields
          show "Not analyzed" (dashed gray) so the panel visually matches
          PeoplePage's 4 MEDDIC chips. Marker: scorecard-always-12-v1 */}
      {showManagerSections && summary && (
        <MeddicBlock meddic={summary.meddic} />
      )}

      {/* Phase-by-phase analysis with coach notes */}
      {showManagerSections && summary?.phase_analysis && Object.keys(summary.phase_analysis).length > 0 && (
        <PhaseAnalysisBlock phases={summary.phase_analysis} />
      )}

      {/* Coaching feedback bullets — "did X → wrong because Y → should be Z" */}
      {showManagerSections && summary?.coaching_feedback && summary.coaching_feedback.length > 0 && (
        <div className="bg-zinc-900/60 border border-zinc-800 border-l-2 border-l-violet-500/60 rounded px-3 py-2 space-y-1.5">
          <div className="text-violet-300 text-[10px] uppercase tracking-wider inline-flex items-center gap-1">
            <Crown className="w-3 h-3" /> Coach's debrief
          </div>
          <ul className="space-y-1.5 text-[12px] text-violet-100">
            {summary.coaching_feedback.map((c, i) => (
              <li key={i} className="leading-snug flex items-start gap-1.5">
                <span className="text-violet-400 shrink-0 mt-0.5">▸</span>
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* What-if alternative play */}
      {showManagerSections && summary?.alternative_play && (
        <div className="bg-zinc-900/60 border border-zinc-800 border-l-2 border-l-cyan-500/60 rounded px-3 py-2 text-[12px]">
          <div className="text-cyan-300 text-[10px] uppercase tracking-wider mb-0.5 inline-flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Alternative play (what-if)
          </div>
          <div className="text-cyan-100 leading-snug">{summary.alternative_play}</div>
        </div>
      )}

      {/* Next-call script — ready-to-use phrases for the manager */}
      {showManagerSections && summary?.next_call_script && (
        summary.next_call_script.goal || summary.next_call_script.opening ||
        (summary.next_call_script.key_questions || []).length > 0 ||
        (summary.next_call_script.anticipated_objections || []).length > 0 ||
        summary.next_call_script.closing
      ) && (
        <NextCallScriptBlock script={summary.next_call_script} />
      )}

      {/* Recommended action — Russian */}
      {summary?.recommended_action && (
        <div className="bg-zinc-900/60 border border-zinc-800 border-l-2 border-l-emerald-500/60 rounded px-3 py-2 text-[12px]">
          <div className="text-emerald-300 text-[10px] uppercase tracking-wider mb-0.5 inline-flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Recommended next action
          </div>
          <div className="text-emerald-100 leading-snug">{summary.recommended_action}</div>
        </div>
      )}

      {/* Ad group verdict — Russian, useful for marketing agency */}
      {summary?.ad_group_verdict && (
        <div className="bg-zinc-900/60 border border-zinc-800 border-l-2 border-l-purple-500/60 rounded px-3 py-2 text-[12px]">
          <div className="text-purple-300 text-[10px] uppercase tracking-wider mb-0.5 inline-flex items-center gap-1">
            <Megaphone className="w-3 h-3" /> Ad group verdict
          </div>
          <div className="text-purple-100 leading-snug">{summary.ad_group_verdict}</div>
        </div>
      )}

      {/* Lost analysis — Russian */}
      {summary?.lost_analysis && (summary.lost_analysis.reason_summary || summary.lost_analysis.what_to_change) && (
        <div className="bg-zinc-900/60 border border-zinc-800 border-l-2 border-l-red-500/60 rounded px-3 py-2 text-[12px] space-y-1">
          <div className="text-red-300 text-[10px] uppercase tracking-wider inline-flex items-center gap-1">
            <TrendingDown className="w-3 h-3" /> Loss analysis
          </div>
          {summary.lost_analysis.reason_summary && (
            <div className="text-red-100 leading-snug">{summary.lost_analysis.reason_summary}</div>
          )}
          {summary.lost_analysis.what_to_change && (
            <div className="text-zinc-300 leading-snug">
              <span className="text-red-400/80 mr-1">What to change:</span> {summary.lost_analysis.what_to_change}
            </div>
          )}
        </div>
      )}

      {/* Flags */}
      {(summary?.green_flags?.length > 0 || summary?.red_flags?.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {summary?.green_flags?.length > 0 && (
            <div className="bg-zinc-900/60 border border-zinc-800 border-l-2 border-l-emerald-500/40 rounded px-2 py-1.5">
              <div className="text-emerald-300 text-[10px] uppercase tracking-wider mb-1">Positive signals</div>
              <ul className="space-y-0.5 text-[11px] text-emerald-200">
                {summary.green_flags.map((f, i) => (
                  <li key={i} className="flex items-start gap-1"><span className="text-emerald-400">+</span><span>{f}</span></li>
                ))}
              </ul>
            </div>
          )}
          {summary?.red_flags?.length > 0 && (
            <div className="bg-zinc-900/60 border border-zinc-800 border-l-2 border-l-red-500/40 rounded px-2 py-1.5">
              <div className="text-red-300 text-[10px] uppercase tracking-wider mb-1">Red flags</div>
              <ul className="space-y-0.5 text-[11px] text-red-200">
                {summary.red_flags.map((f, i) => (
                  <li key={i} className="flex items-start gap-1"><span className="text-red-400">−</span><span>{f}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Ukrainian translation (collapsed by default) */}
      {summary?.summary_uk && (
        <details className="text-zinc-500">
          <summary className="cursor-pointer hover:text-zinc-400 text-[10px] uppercase tracking-wider inline-flex items-center gap-1">
            <Languages className="w-3 h-3" /> Ukrainian version
          </summary>
          <div className="mt-1 text-zinc-300 leading-relaxed whitespace-pre-wrap text-[12px]">
            {summary.summary_uk}
          </div>
        </details>
      )}

      {summary?.generated_at && (
        <div className="text-[10px] text-zinc-600">
          Generated {new Date(summary.generated_at).toLocaleString()}
          {summary.model && ` · ${summary.model}`}
        </div>
      )}
    </div>
  );
};

// Grade visual map for the campaign-verdict A-F badge.
const GRADE_META = {
  A: { cls: "bg-emerald-500/30 text-emerald-100 border-emerald-400/50", label: "A — strong campaign" },
  B: { cls: "bg-cyan-500/25 text-cyan-100 border-cyan-400/40", label: "B — solid" },
  C: { cls: "bg-zinc-500/25 text-zinc-200 border-zinc-500/40", label: "C — average" },
  D: { cls: "bg-amber-500/25 text-amber-100 border-amber-400/40", label: "D — weak" },
  F: { cls: "bg-red-500/30 text-red-100 border-red-400/50", label: "F — failing" },
};

const SCALING_META = {
  scale: { label: "Scale up", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", icon: TrendingUp },
  maintain: { label: "Maintain", cls: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30", icon: ShieldCheck },
  optimize: { label: "Optimize", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30", icon: Wand2 },
  pause: { label: "Pause", cls: "bg-red-500/15 text-red-300 border-red-500/30", icon: ThumbsDown },
};

/**
 * Per-campaign deep-dive panel. Shows funnel by CRM pipeline stage,
 * manager × stage breakdown for this campaign, and an AI-generated overall
 * verdict (grade A-F + recommendations for marketing agency and sales).
 */
const CampaignDeepDive = ({ campaign, data, isGenerating, onGenerate }) => {
  if (!data) {
    return (
      <div className="px-4 pt-3 text-[11px] text-zinc-500">
        <Loader2 className="w-3 h-3 inline-block animate-spin mr-1" />
        Loading campaign deep-dive…
      </div>
    );
  }
  const verdict = data.verdict;
  const funnel = data.funnel || [];
  const managers = data.managers || [];
  const grade = verdict?.campaign_grade;
  const gradeMeta = grade ? GRADE_META[grade] : null;
  const scaling = verdict?.scaling_recommendation;
  const scalingMeta = scaling ? SCALING_META[scaling] : null;
  const ScalingIcon = scalingMeta?.icon;
  const maxFunnelCount = Math.max(1, ...funnel.map((s) => s.count || 0));

  return (
    <div className="px-4 pt-4 pb-2 space-y-4">
      {/* AI verdict header — generate button or rendered verdict */}
      <div className="bg-purple-500/5 border border-purple-500/25 rounded-lg p-3">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-300" />
            <span className="text-purple-200 font-medium text-sm">Campaign verdict (AI)</span>
            {gradeMeta && (
              <span className={`inline-flex items-center px-2.5 py-0.5 text-sm font-bold rounded border ${gradeMeta.cls}`}
                title={gradeMeta.label}>
                {grade}
              </span>
            )}
            {scalingMeta && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded border ${scalingMeta.cls}`}>
                {ScalingIcon && <ScalingIcon className="w-3 h-3" />}
                {scalingMeta.label}
              </span>
            )}
          </div>
          <Button
            size="sm"
            variant="outline"
            disabled={isGenerating}
            onClick={() => onGenerate(!!verdict)}
            className="border-purple-700/50 hover:bg-purple-500/10 text-purple-300 h-7"
          >
            {isGenerating ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Generating…</>
            ) : verdict ? (
              <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Regenerate verdict</>
            ) : (
              <><Wand2 className="w-3.5 h-3.5 mr-1.5" /> Generate verdict</>
            )}
          </Button>
        </div>

        {!verdict && !isGenerating && (
          <div className="text-[12px] text-zinc-400 italic">
            Click <strong>Generate verdict</strong> to get an AI campaign assessment: grade A-F, what's working, what's broken,
            and concrete recommendations for both the marketing agency and sales managers.
            Cost ~$0.001 per campaign. For best quality, run <em>Analyze all leads</em> on the leaderboard first so
            the verdict can synthesize per-lead quality data.
          </div>
        )}

        {verdict && (
          <div className="space-y-2 text-[12px]">
            {/* Russian summary */}
            {verdict.summary_ru && (
              <div className="text-zinc-100 leading-relaxed whitespace-pre-wrap">{verdict.summary_ru}</div>
            )}

            {/* Strengths / weaknesses */}
            {(verdict.key_strengths?.length > 0 || verdict.key_weaknesses?.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                {verdict.key_strengths?.length > 0 && (
                  <div className="bg-emerald-500/5 border border-emerald-500/20 rounded p-2">
                    <div className="text-emerald-300 text-[10px] uppercase tracking-wider mb-1">Strengths</div>
                    <ul className="space-y-0.5 text-[11px] text-emerald-200">
                      {verdict.key_strengths.map((s, i) => (
                        <li key={i} className="flex items-start gap-1"><span className="text-emerald-400">+</span><span>{s}</span></li>
                      ))}
                    </ul>
                  </div>
                )}
                {verdict.key_weaknesses?.length > 0 && (
                  <div className="bg-red-500/5 border border-red-500/20 rounded p-2">
                    <div className="text-red-300 text-[10px] uppercase tracking-wider mb-1">Weaknesses</div>
                    <ul className="space-y-0.5 text-[11px] text-red-200">
                      {verdict.key_weaknesses.map((w, i) => (
                        <li key={i} className="flex items-start gap-1"><span className="text-red-400">−</span><span>{w}</span></li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Manager observations */}
            {verdict.manager_observations && (
              <div className="bg-zinc-800/40 border border-zinc-700 rounded p-2">
                <div className="text-zinc-400 text-[10px] uppercase tracking-wider mb-0.5">Manager observations</div>
                <div className="text-zinc-200 leading-snug">{verdict.manager_observations}</div>
              </div>
            )}

            {/* Recommendations split between agency + sales */}
            {(verdict.recommendations_for_agency?.length > 0 || verdict.recommendations_for_sales?.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                {verdict.recommendations_for_agency?.length > 0 && (
                  <div className="bg-blue-500/5 border border-blue-500/25 rounded p-2">
                    <div className="text-blue-300 text-[10px] uppercase tracking-wider mb-1 inline-flex items-center gap-1">
                      <Megaphone className="w-3 h-3" /> For marketing agency
                    </div>
                    <ul className="space-y-0.5 text-[11px] text-blue-100">
                      {verdict.recommendations_for_agency.map((r, i) => (
                        <li key={i} className="flex items-start gap-1"><span className="text-blue-400">→</span><span>{r}</span></li>
                      ))}
                    </ul>
                  </div>
                )}
                {verdict.recommendations_for_sales?.length > 0 && (
                  <div className="bg-cyan-500/5 border border-cyan-500/25 rounded p-2">
                    <div className="text-cyan-300 text-[10px] uppercase tracking-wider mb-1 inline-flex items-center gap-1">
                      <Award className="w-3 h-3" /> For sales managers
                    </div>
                    <ul className="space-y-0.5 text-[11px] text-cyan-100">
                      {verdict.recommendations_for_sales.map((r, i) => (
                        <li key={i} className="flex items-start gap-1"><span className="text-cyan-400">→</span><span>{r}</span></li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {verdict.summary_uk && (
              <details className="text-zinc-500 mt-1">
                <summary className="cursor-pointer hover:text-zinc-400 text-[10px] uppercase tracking-wider inline-flex items-center gap-1">
                  <Languages className="w-3 h-3" /> Ukrainian version
                </summary>
                <div className="mt-1 text-zinc-300 leading-relaxed whitespace-pre-wrap text-[12px]">
                  {verdict.summary_uk}
                </div>
              </details>
            )}

            {verdict.generated_at && (
              <div className="text-[10px] text-zinc-600">
                Generated {new Date(verdict.generated_at).toLocaleString()}
                {verdict.model && ` · ${verdict.model}`}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Funnel visualization (horizontal bars, sorted by win-prob desc) */}
      {funnel.length > 0 && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 inline-flex items-center gap-1">
            <Target className="w-3 h-3" /> Pipeline funnel
          </div>
          <div className="space-y-1">
            {funnel.map((s) => {
              const widthPct = Math.max(4, (s.count / maxFunnelCount) * 100);
              const cls = stageBadgeFor(s.status_class, s.probability);
              return (
                <div key={s.stage_id} className="flex items-center gap-2 text-[11px]">
                  <div className="w-44 truncate text-zinc-300 shrink-0" title={s.label}>
                    {s.label}
                    {s.probability != null && (
                      <span className="text-zinc-600 ml-1">{(s.probability * 100).toFixed(0)}%</span>
                    )}
                  </div>
                  <div className="flex-1 h-5 bg-zinc-800 rounded relative overflow-hidden">
                    <div
                      className={`h-full ${cls.split(" ").filter((x) => x.startsWith("bg-")).join(" ")} opacity-70`}
                      style={{ width: `${widthPct}%` }}
                    />
                    <span className="absolute inset-0 flex items-center px-2 text-[11px] text-zinc-50 font-mono">
                      {s.count} {s.count === 1 ? "deal" : "deals"} · ${s.value_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      {s.drop_pct_from_prev != null && s.drop_pct_from_prev > 0 && (
                        <span className="ml-2 text-amber-300/80">↓ {s.drop_pct_from_prev}% from prev</span>
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Manager × stage breakdown for this campaign */}
      {managers.length > 0 && (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 inline-flex items-center gap-1">
            <Users className="w-3 h-3" /> Manager activity
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead className="text-zinc-500">
                <tr>
                  <th className="px-2 py-1 text-left">Manager</th>
                  <th className="px-2 py-1 text-center">Leads</th>
                  <th className="px-2 py-1 text-center">Deals</th>
                  <th className="px-2 py-1 text-center">Won</th>
                  <th className="px-2 py-1 text-center">Lost</th>
                  <th className="px-2 py-1 text-center">Open</th>
                  <th className="px-2 py-1 text-right">Won $</th>
                  <th className="px-2 py-1 text-left">Stage breakdown</th>
                </tr>
              </thead>
              <tbody>
                {managers.map((mb) => (
                  <tr key={mb.owner_id} className="border-t border-zinc-800/60">
                    <td className="px-2 py-1 text-zinc-200 max-w-[180px] truncate" title={mb.name}>{mb.name}</td>
                    <td className="px-2 py-1 text-center text-emerald-300">{mb.leads || 0}</td>
                    <td className="px-2 py-1 text-center text-zinc-300">{mb.deals || 0}</td>
                    <td className="px-2 py-1 text-center text-emerald-400">{mb.won || 0}</td>
                    <td className="px-2 py-1 text-center text-red-400/80">{mb.lost || 0}</td>
                    <td className="px-2 py-1 text-center text-blue-300/80">{mb.open || 0}</td>
                    <td className="px-2 py-1 text-right text-emerald-300 font-mono">
                      ${(mb.won_value_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="px-2 py-1 text-zinc-400">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(mb.by_stage || {}).map(([sid, sv]) => (
                          <span key={sid} className="inline-flex items-center px-1.5 py-0 bg-zinc-800 rounded text-[10px]">
                            {sv.label}: {sv.count}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const LeaderboardColumn = ({ title, subtitle, icon: Icon, iconCls, rows, onOpen, variant = "top" }) => (
  <div className="bg-zinc-950/40 border border-zinc-800 rounded-lg p-3">
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-4 h-4 ${iconCls}`} />
      <div>
        <div className="text-zinc-100 text-sm font-medium">{title}</div>
        <div className="text-zinc-500 text-[11px]">{subtitle}</div>
      </div>
    </div>
    {rows.length === 0 ? (
      <div className="text-zinc-500 text-xs py-3">Nothing here yet.</div>
    ) : (
      <div className="space-y-1">
        {rows.map((r) => {
          const tier = r.quality_tier;
          const reason = r.disqualification_reason || "";
          return (
            <button
              key={r.deal_id}
              type="button"
              onClick={() => onOpen(r)}
              className="w-full text-left bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900 rounded p-2 text-xs space-y-1 transition-colors"
            >
              {/* Row 1: score chip + deal name (truncated) + amount */}
              <div className="flex items-center justify-between gap-2 min-w-0">
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border shrink-0 ${tierClsFor(tier)}`}>
                  <span className="font-semibold">{r.quality_score ?? "—"}</span>
                  <span className="opacity-80">·</span>
                  <span>{tierLabelFor(tier)}</span>
                </span>
                <span className="text-zinc-200 font-medium truncate flex-1 min-w-0">{r.deal_name || "(unnamed deal)"}</span>
                {r.overall_manager_score != null && variant === "top" && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border shrink-0 ${scoreBgClass(r.overall_manager_score)}`}>
                        <Users className="w-2.5 h-2.5" />
                        <span className="font-semibold">{r.overall_manager_score}</span>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-[11px]">
                      Manager overall score — weighted avg of 8 scorecard metrics
                    </TooltipContent>
                  </Tooltip>
                )}
                {r.amount_usd > 0 && (
                  <span className="text-emerald-300 font-mono shrink-0 text-[11px]">
                    ${r.amount_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                )}
              </div>

              {/* Row 2: disqualification reason if any. Manager scorecard
                  chips were removed 2026-05-24 (qualified-binary-v1) — they
                  belong on the per-lead detail modal and on /people, not
                  inline on every leaderboard row. */}
              {reason ? (
                <div className="flex items-start gap-1.5 text-[11px] text-red-300 bg-red-500/5 border border-red-500/20 rounded px-1.5 py-1">
                  <ThumbsDown className="w-3 h-3 mt-0.5 shrink-0 text-red-400/80" />
                  <span className="leading-snug">{reason}</span>
                </div>
              ) : null}

              {/* Row 3: campaign + stage + owner — compact meta */}
              <div className="flex items-center gap-2 text-[10px] text-zinc-500 flex-wrap min-w-0">
                <span className="inline-flex items-center gap-1 truncate max-w-[40%]" title={r.campaign || "Untagged"}>
                  <Megaphone className="w-2.5 h-2.5 shrink-0" /> <span className="truncate">{r.campaign || "Untagged"}</span>
                </span>
                {r.stage_label && (
                  <span className={`px-1 py-0 rounded ${stageBadgeFor(r.stage_status, null)}`}>
                    {r.stage_label}
                  </span>
                )}
                {r.owner?.name && <span className="truncate">· {r.owner.name}</span>}
              </div>
            </button>
          );
        })}
      </div>
    )}
  </div>
);

// =============================================================================
// AI Lead Coach — Gong/Chorus-inspired UI (replaces LeaderboardColumn).
//
// CoachCard:  single-row dense lead summary (avatar + name + meta + 4 key
//             metric bars + tier chip + 1-line coach takeaway). Click → drawer.
// CoachDrawer: right-slide panel with the full v7 AiQualityPanel content +
//             prev/next nav. Replaces the centered modal that needed close+
//             reopen for each lead.
// =============================================================================

// Pick 4 most actionable metrics to show inline on the card. The rest live
// inside the drawer.
const CARD_METRICS = [
  { key: "response_speed", label: "Speed" },
  { key: "discovery",      label: "Disc." },
  { key: "budget_talk",    label: "Budget" },
  { key: "follow_up",      label: "F-up" },
];

function _initials(name) {
  if (!name) return "??";
  // Strip "Landing Lead — " prefix and "[FORM_LEAD]" suffix so the
  // initials reflect the actual person/company name, not the boilerplate.
  let cleaned = String(name)
    .replace(/^Landing\s+Lead\s*[—–\-]*\s*/i, "")
    .replace(/\[[^\]]+\]/g, "")
    .replace(/[—–]/g, " ")
    .trim();
  if (!cleaned) cleaned = String(name);
  const parts = cleaned.split(/\s+/).filter((p) => /[a-zа-яії]/i.test(p));
  if (parts.length === 0) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function _avatarBgFromName(name) {
  // Stable hash → tailwind hue for the avatar bg.
  let h = 0;
  for (const c of (name || "")) h = (h * 31 + c.charCodeAt(0)) | 0;
  const palette = [
    "bg-blue-500/30 text-blue-200",
    "bg-emerald-500/30 text-emerald-200",
    "bg-cyan-500/30 text-cyan-200",
    "bg-amber-500/30 text-amber-200",
    "bg-rose-500/30 text-rose-200",
    "bg-violet-500/30 text-violet-200",
    "bg-orange-500/30 text-orange-200",
    "bg-teal-500/30 text-teal-200",
  ];
  return palette[Math.abs(h) % palette.length];
}

// =============================================================================
// CampaignsQualifiedSection — qualified-binary-v1 (Decision 2026-05-24)
// Binary collapse of the per-lead AI scoring into a campaign-level view:
// emerald qualified / red trash / amber pending. Expanded row shows
// qualified-by-stage (pipeline progression) + qualified-by-manager slice.
// Rich per-lead narrative (marketing.grade, sales.grade, scorecard, MEDDIC)
// stays on the Lead Detail modal. Full manager scorecards live on /people.
// =============================================================================

const _binaryFmtUsd = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const _BinaryStat = ({ label, value, cls }) => (
  <div className={`border rounded-lg p-2.5 ${cls}`}>
    <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
    <div className="text-2xl font-semibold mt-0.5">{value}</div>
  </div>
);

const _BinaryChip = ({ label, count, pct, tone, onClick, active }) => {
  const palette = {
    emerald: {
      base: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200",
      hover: "hover:bg-emerald-500/25",
      active: "bg-emerald-500/35 border-emerald-400 text-emerald-50 ring-1 ring-emerald-400/60",
    },
    red: {
      base: "bg-red-500/15 border-red-500/40 text-red-200",
      hover: "hover:bg-red-500/25",
      active: "bg-red-500/35 border-red-400 text-red-50 ring-1 ring-red-400/60",
    },
    zinc: {
      base: "bg-zinc-800 border-zinc-700 text-zinc-400",
      hover: "hover:bg-zinc-700",
      active: "bg-zinc-700 border-zinc-500 text-zinc-100 ring-1 ring-zinc-500",
    },
    amber: {
      base: "bg-amber-500/15 border-amber-500/40 text-amber-200",
      hover: "hover:bg-amber-500/25",
      active: "bg-amber-500/35 border-amber-400 text-amber-50 ring-1 ring-amber-400/60",
    },
  }[tone] || {
    base: "bg-zinc-800 border-zinc-700 text-zinc-400",
    hover: "hover:bg-zinc-700",
    active: "bg-zinc-700 border-zinc-500 text-zinc-100 ring-1 ring-zinc-500",
  };
  const cls = active ? palette.active : `${palette.base} ${onClick ? palette.hover : ""}`;
  const body = (
    <>
      <span className="font-semibold tabular-nums">{count}</span>
      <span className="opacity-80">{label}</span>
      {pct != null && <span className="opacity-60 text-[10px]">· {pct}%</span>}
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded border transition cursor-pointer ${cls}`}
      >
        {body}
      </button>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded border ${cls}`}>
      {body}
    </span>
  );
};

// Mirror of backend _derive_qualified — returns "qualified" | "trash" |
// "insufficient" | "none" (no AI summary yet). Single source of truth for
// the filter/list logic.
const _classifyLead = (ai) => {
  if (!ai || Object.keys(ai).length === 0) return "none";
  if (ai.qualified === true) return "qualified";
  if (ai.qualified === false) return "trash";
  const grade = (ai.marketing || {}).grade;
  if (grade === "Excellent" || grade === "Good" || grade === "Average") return "qualified";
  if (grade === "Weak" || grade === "Bad") return "trash";
  if (grade === "Insufficient data") return "insufficient";
  if (ai.unreachable_lead) return "insufficient";
  const tier = (ai.quality_tier || "").toLowerCase();
  if (["premium", "quality", "average"].includes(tier)) return "qualified";
  if (["weak", "junk"].includes(tier)) return "trash";
  return "insufficient";
};

// Short human-readable reason for why this lead landed in trash or
// insufficient. Pulls from the richest available AI field; falls back to
// generic message if nothing was generated.
const _whyVerdict = (ai, bucket) => {
  if (!ai) return "";
  if (bucket === "trash") {
    const reasons = [];
    if (ai.disqualification_reason) reasons.push(ai.disqualification_reason);
    const m = ai.marketing || {};
    if (m.reasoning && m.reasoning !== reasons[0]) reasons.push(m.reasoning);
    if (!reasons.length && (ai.red_flags || []).length > 0) {
      reasons.push(ai.red_flags.slice(0, 2).join(" · "));
    }
    if (!reasons.length && ai.lost_analysis?.reason_summary) {
      reasons.push(ai.lost_analysis.reason_summary);
    }
    return reasons.join(" — ") || "AI graded as trash — no reasoning text generated";
  }
  if (bucket === "insufficient") {
    if (ai.unreachable_lead) {
      return "Lead never responded — manager attempted contact but no conversation happened, so quality can't be judged";
    }
    const m = ai.marketing || {};
    if (m.grade === "Insufficient data" && m.reasoning) return m.reasoning;
    if (ai.attribution_note) return ai.attribution_note;
    return "Not enough signal to grade — too little info in form or no contact yet";
  }
  return "";
};

const _StageChip = ({ stage }) => {
  const p = stage.probability || 0;
  let cls;
  if (p >= 0.9) cls = "bg-emerald-500/15 border-emerald-500/40 text-emerald-200";
  else if (p >= 0.5) cls = "bg-cyan-500/15 border-cyan-500/40 text-cyan-200";
  else if (p >= 0.3) cls = "bg-amber-500/15 border-amber-500/40 text-amber-200";
  else cls = "bg-zinc-800 border-zinc-700 text-zinc-300";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded border ${cls}`}
      title={`Stage probability: ${Math.round(p * 100)}%`}
    >
      <span>{stage.label}</span>
      <span className="opacity-50">·</span>
      <span className="font-semibold tabular-nums">{stage.count}</span>
    </span>
  );
};

const CampaignsQualifiedSection = ({
  campaigns,
  owners,
  dateFrom,
  dateTo,
  leadsByCampaign,
  loadingCampaigns,
  onEnsureLeads,
  onOpenLead,
}) => {
  void owners; // reserved — used to be needed for the by-manager agg we replaced.

  const rows = useMemo(() => {
    return (campaigns || [])
      .map((c) => ({
        ...c,
        q: c.quality || {},
        // "Not analyzed" now means nobody judged the lead — neither the
        // manager in the CRM nor our AI. It used to be derived as
        // contacts − AI-graded, which counted a manager's own verdict as
        // "not analyzed" and made the page look emptier than the CRM.
        notAnalyzed: (c.quality || {}).not_analyzed_count ?? 0,
      }))
      // A campaign with CRM deals but no attributed contacts is still a real
      // campaign — keep it, or its spend silently leaves the table.
      .filter((c) => (c.contacts || 0) > 0 || (c.q.leads || 0) > 0 || (c.q.summarized || 0) > 0)
      .sort((a, b) => (b.q.qualified_count || 0) - (a.q.qualified_count || 0));
  }, [campaigns]);

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          qualified: acc.qualified + (r.q.qualified_count || 0),
          trash: acc.trash + (r.q.trash_count || 0),
          insufficient: acc.insufficient + (r.q.insufficient_count || 0),
          not_analyzed: acc.not_analyzed + r.notAnalyzed,
          from_crm: acc.from_crm + (r.q.crm_verdict_count || 0),
          spend: acc.spend + (r.spend_usd || 0),
        }),
        { qualified: 0, trash: 0, insufficient: 0, not_analyzed: 0, from_crm: 0, spend: 0 },
      ),
    [rows],
  );

  const [expanded, setExpanded] = useState(new Set());
  // Per-campaign filter bucket: "qualified" (default) | "trash" | "not_analyzed".
  // Clicking a chip on a row both expands it and switches the filter.
  const [bucketByCampaign, setBucketByCampaign] = useState({});

  const ensureExpandedAndLoaded = (name) => {
    setExpanded((prev) => {
      if (prev.has(name)) return prev;
      const next = new Set(prev);
      next.add(name);
      return next;
    });
    if (typeof onEnsureLeads === "function") onEnsureLeads(name);
  };

  const toggle = (name) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
        if (typeof onEnsureLeads === "function") onEnsureLeads(name);
      }
      return next;
    });
  };

  const setBucket = (campaign, bucket, event) => {
    if (event) event.stopPropagation();
    setBucketByCampaign((prev) => ({ ...prev, [campaign]: bucket }));
    ensureExpandedAndLoaded(campaign);
  };

  // Judged = the CRM answered Yes or No. Pending is not a denominator.
  const qualifiedTotalGraded = totals.qualified + totals.trash;
  const overallQualPct = qualifiedTotalGraded
    ? Math.round((totals.qualified / qualifiedTotalGraded) * 100)
    : 0;

  return (
    <Card className="bg-zinc-900/50 border-zinc-800" data-section="qualified-binary-v1">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between gap-3 text-base flex-wrap">
          <span className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            Qualified leads by campaign
          </span>
          <span className="text-xs text-zinc-500 font-normal">
            Verdict comes from the <span className="text-emerald-300">Qualified</span>{" "}
            field on the deal in the CRM — nothing else votes. Our AI analysis is
            still on the lead panel, as description, not as a verdict. Manager
            scorecards on{" "}
            <Link
              to={`/marketing-analytics/people?from=${dateFrom}&to=${dateTo}`}
              className="text-blue-300 hover:underline"
            >
              People
            </Link>
            .
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <_BinaryStat
            label="Total spend"
            value={_binaryFmtUsd(totals.spend)}
            cls="bg-blue-500/10 border-blue-500/30 text-blue-100"
          />
          <_BinaryStat
            label="Cost / qualified"
            value={totals.qualified > 0 ? _binaryFmtUsd(totals.spend / totals.qualified) : "—"}
            cls="bg-violet-500/10 border-violet-500/30 text-violet-100"
          />
          <_BinaryStat
            label="Qualified"
            value={totals.qualified}
            cls="bg-emerald-500/10 border-emerald-500/30 text-emerald-100"
          />
          <_BinaryStat
            label="Trash"
            value={totals.trash}
            cls="bg-red-500/10 border-red-500/30 text-red-100"
          />
          <_BinaryStat
            label="Not judged yet"
            value={totals.not_analyzed}
            cls="bg-amber-500/10 border-amber-500/30 text-amber-100"
          />
        </div>
        {qualifiedTotalGraded > 0 && (
          <div className="text-[11px] text-zinc-500">
            Overall qualified rate:{" "}
            <span className="text-emerald-300 font-semibold">{overallQualPct}%</span>{" "}
            ({totals.qualified} of {qualifiedTotalGraded} judged leads).{" "}
            {totals.from_crm > 0 && (
              <>
                all of them judged in the CRM — the{" "}
                <span className="text-zinc-400">Qualified</span> field on the deal is the
                only thing that decides this.{" "}
              </>
            )}
            Spend includes campaigns without any leads yet.
          </div>
        )}

        <div className="space-y-2">
          {rows.length === 0 ? (
            <div className="text-zinc-500 text-sm italic py-4 text-center">
              No campaigns with leads in this date range.
            </div>
          ) : (
            rows.map((r) => {
              const totalGraded =
                (r.q.qualified_count || 0) + (r.q.trash_count || 0) + (r.q.insufficient_count || 0);
              const qualPct = totalGraded
                ? Math.round(((r.q.qualified_count || 0) / totalGraded) * 100)
                : null;
              const isExpanded = expanded.has(r.campaign);
              const allLeads = leadsByCampaign?.[r.campaign] || null;
              const isLoadingLeads = loadingCampaigns?.has?.(r.campaign);
              return (
                <div
                  key={r.campaign}
                  className="border border-zinc-800 rounded-lg bg-zinc-950/40 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => toggle(r.campaign)}
                    className="w-full text-left p-3 hover:bg-zinc-900/40 transition flex items-center gap-3"
                  >
                    <ChevronRight
                      className={`w-4 h-4 shrink-0 text-zinc-500 transition ${isExpanded ? "rotate-90" : ""}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-zinc-100 truncate">{r.campaign}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>{_binaryFmtUsd(r.spend_usd)} spend</span>
                        {/* Meta spells a campaign one way, the landing tags it
                            another. When we fold the two together, say so —
                            spend that appears out of nowhere is as suspicious
                            as spend that goes missing. */}
                        {(r.aliases || []).length > 0 && (
                          <span className="text-zinc-600" title={`Meta campaign: ${(r.aliases || []).join(", ")}`}>
                            (incl. {(r.aliases || []).join(", ")})
                          </span>
                        )}
                        <span className="opacity-50">·</span>
                        <span>{r.q.leads ?? r.contacts ?? 0} leads</span>
                        <span className="opacity-50">·</span>
                        <span>
                          {(r.q.crm_verdict_count || 0) > 0
                            ? `${r.q.crm_verdict_count} judged in CRM`
                            : `${r.q.summarized || 0} analyzed`}
                        </span>
                        {r.notAnalyzed > 0 && (
                          <span className="text-amber-400">
                            <span className="opacity-50">·</span> {r.notAnalyzed} not analyzed
                          </span>
                        )}
                        {/* Cached analyses whose deal no longer exists — the
                            HubSpot-era leftovers. They used to be counted as
                            leads, which is how a "12 qualified" chip sat above
                            a list of 7. */}
                        {(r.q.stale_summaries || 0) > 0 && (
                          <span className="text-zinc-600" title="Cached AI analyses of deals that no longer exist (pre-CRM-migration). Re-analyze to refresh.">
                            <span className="opacity-50">·</span> {r.q.stale_summaries} stale
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                      <_BinaryChip
                        label="qualified"
                        count={r.q.qualified_count || 0}
                        pct={qualPct}
                        tone="emerald"
                        active={(bucketByCampaign[r.campaign] || "qualified") === "qualified"}
                        onClick={(e) => setBucket(r.campaign, "qualified", e)}
                      />
                      {(r.q.pipeline_override_count || 0) > 0 && (
                        <span
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border bg-violet-500/10 border-violet-500/40 text-violet-200"
                          title={`Pipeline-override: ${r.q.pipeline_override_count} of qualified came from pipeline stage progression (AI verdict was less generous). Click the qualified chip to see them — they'll show a 'pipeline' badge.`}
                        >
                          ↑{r.q.pipeline_override_count}
                        </span>
                      )}
                      <_BinaryChip
                        label="trash"
                        count={r.q.trash_count || 0}
                        tone="red"
                        active={bucketByCampaign[r.campaign] === "trash"}
                        onClick={(e) => setBucket(r.campaign, "trash", e)}
                      />
{r.notAnalyzed > 0 && (
                        <_BinaryChip
                          label="pending"
                          count={r.notAnalyzed}
                          tone="amber"
                          active={bucketByCampaign[r.campaign] === "not_analyzed"}
                          onClick={(e) => setBucket(r.campaign, "not_analyzed", e)}
                        />
                      )}
                    </div>
                  </button>

                  {isExpanded && (() => {
                    const activeBucket = bucketByCampaign[r.campaign] || "qualified";
                    // Backend already classified each lead — trust its bucket.
                    const filteredLeads = (allLeads || []).filter(
                      (l) => l.bucket === activeBucket,
                    );
                    const bucketMeta = {
                      qualified: {
                        title: "Qualified leads",
                        empty: "No qualified leads in this date range.",
                        showWhy: false,
                        showStageStrip: true,
                      },
                      trash: {
                        title: "Trash leads — not worth pursuing (see WHY for who decided)",
                        empty: "No trash leads in this date range.",
                        showWhy: true,
                        showStageStrip: false,
                      },
                      // Nobody judged these — not the manager, not our AI.
                      // Kept apart from "insufficient", which is a verdict.
                      not_analyzed: {
                        title: "Pending — no «Qualified» answer on the deal in the CRM yet",
                        empty: "Nothing pending in this date range.",
                        showWhy: true,
                        showStageStrip: false,
                      },
                    }[activeBucket] || {
                      title: "Leads",
                      empty: "No leads in this date range.",
                      showWhy: true,
                      showStageStrip: false,
                    };
                    return (
                    <div className="border-t border-zinc-800 p-3 space-y-3 bg-zinc-950/60">
                      {bucketMeta.showStageStrip && (r.q.qualified_by_stage || []).length > 0 && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
                            Qualified leads — pipeline stage
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {r.q.qualified_by_stage.map((s) => (
                              <_StageChip key={s.stage_id} stage={s} />
                            ))}
                          </div>
                        </div>
                      )}

                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-2">
                          <span>{bucketMeta.title} ({filteredLeads.length})</span>
                          {isLoadingLeads && !allLeads && (
                            <Loader2 className="w-3 h-3 animate-spin text-zinc-600" />
                          )}
                        </div>
                        {!allLeads && isLoadingLeads ? (
                          <div className="text-xs text-zinc-600 italic py-2">Loading leads…</div>
                        ) : filteredLeads.length === 0 ? (
                          <div className="text-xs text-zinc-600 italic py-2">
                            {bucketMeta.empty}
                          </div>
                        ) : (
                          <div className="overflow-hidden border border-zinc-800 rounded">
                            <table className="w-full text-xs">
                              <thead className="bg-zinc-900/60 text-zinc-500 text-[10px] uppercase tracking-wider">
                                <tr>
                                  <th className="text-left px-2 py-1.5 font-medium">Lead</th>
                                  <th className="text-left px-2 py-1.5 font-medium">Stage</th>
                                  <th className="text-right px-2 py-1.5 font-medium">Amount</th>
                                  <th className="text-left px-2 py-1.5 font-medium">Manager</th>
                                  {bucketMeta.showWhy && (
                                    <th className="text-left px-2 py-1.5 font-medium">Why</th>
                                  )}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-zinc-800">
                                {filteredLeads.map((l) => {
                                  const deal = l.deal || {};
                                  const contact = l.contact || {};
                                  const owner = l.owner || {};
                                  const ai = l.ai_summary || {};
                                  const stageLabel = deal.stage_label || "—";
                                  const stageStatus = deal.stage_status || "open";
                                  const stageProb = deal.stage_probability || 0;
                                  let stageCls;
                                  if (stageStatus === "won")
                                    stageCls =
                                      "bg-emerald-500/15 border-emerald-500/40 text-emerald-200";
                                  else if (stageStatus === "lost")
                                    stageCls = "bg-red-500/15 border-red-500/40 text-red-200";
                                  else if (stageProb >= 0.5)
                                    stageCls = "bg-cyan-500/15 border-cyan-500/40 text-cyan-200";
                                  else if (stageProb >= 0.3)
                                    stageCls = "bg-amber-500/15 border-amber-500/40 text-amber-200";
                                  else
                                    stageCls = "bg-zinc-800 border-zinc-700 text-zinc-300";
                                  const amount = deal.amount_usd || 0;
                                  const displayName = deal.name || contact.name || "(unnamed)";
                                  const cleanName = displayName
                                    .replace(/^Landing\s+Lead\s*[—–\-]*\s*/i, "")
                                    .replace(/\[[^\]]+\]/g, "")
                                    .trim() || displayName;
                                  const dealUrl =
                                    deal.crm_id
                                      ? crmUrl("deal", deal.crm_id)
                                      : null;
                                  const why = bucketMeta.showWhy
                                    ? (l.verdict_reason || _whyVerdict(ai, activeBucket))
                                    : "";
                                  const openModal = () => {
                                    if (typeof onOpenLead === "function") onOpenLead(l, r.campaign);
                                  };
                                  return (
                                    <tr
                                      key={deal.id || deal.crm_id || cleanName}
                                      onClick={openModal}
                                      className="hover:bg-zinc-900/40 align-top cursor-pointer"
                                      title="Click to open full AI analysis"
                                    >
                                      <td className="px-2 py-1.5">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); openModal(); }}
                                            className="text-zinc-100 hover:text-blue-300 text-left truncate inline-flex items-center gap-1"
                                          >
                                            <span className="truncate">{cleanName}</span>
                                            <Sparkles className="w-3 h-3 opacity-40 shrink-0" />
                                          </button>
                                          {l.is_returning && (
                                            <span
                                              className="inline-flex items-center px-1 py-0 rounded text-[9px] uppercase tracking-wider bg-violet-500/15 border border-violet-500/40 text-violet-200"
                                              title="Returning customer — contact existed in the CRM before this date range; new deal was attributed to this ad"
                                            >
                                              returning
                                            </span>
                                          )}
                                          <LeadTypeBadge
                                            leadType={(l.deal || {}).lead_type}
                                            dealName={(l.deal || {}).name}
                                          />
                                          {l.verdict_source === "crm" && (
                                            <span
                                              className="inline-flex items-center px-1 py-0 rounded text-[9px] uppercase tracking-wider bg-emerald-500/15 border border-emerald-500/40 text-emerald-200"
                                              title={`The manager's own answer in the CRM: qualified = ${(l.deal || {}).qualified || "—"}`}
                                            >
                                              crm
                                            </span>
                                          )}
                                          {l.verdict_source === "pipeline_override" && (
                                            <span
                                              className="inline-flex items-center px-1 py-0 rounded text-[9px] uppercase tracking-wider bg-cyan-500/15 border border-cyan-500/40 text-cyan-200"
                                              title="AI verdict was less generous (insufficient/trash), but pipeline stage progression proves the lead was engaged — upgraded to qualified"
                                            >
                                              pipeline
                                            </span>
                                          )}
                                          {dealUrl && (
                                            <a
                                              href={dealUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              onClick={(e) => e.stopPropagation()}
                                              className="text-zinc-500 hover:text-blue-300 shrink-0"
                                              title="Open in the CRM"
                                            >
                                              <ExternalLink className="w-3 h-3" />
                                            </a>
                                          )}
                                        </div>
                                      </td>
                                      <td className="px-2 py-1.5">
                                        <span
                                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] ${stageCls}`}
                                          title={`Probability: ${Math.round(stageProb * 100)}%`}
                                        >
                                          {stageLabel}
                                        </span>
                                      </td>
                                      <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">
                                        {amount > 0 ? (
                                          <span className="text-emerald-300 font-mono">
                                            {_binaryFmtUsd(amount)}
                                          </span>
                                        ) : (
                                          <span className="text-zinc-600">—</span>
                                        )}
                                      </td>
                                      <td className="px-2 py-1.5 text-zinc-300 whitespace-nowrap">
                                        {owner.name || "—"}
                                      </td>
                                      {bucketMeta.showWhy && (
                                        <td className="px-2 py-1.5 text-zinc-400 leading-snug max-w-[420px]">
                                          {why || (
                                            <span className="italic text-zinc-600">no reason recorded</span>
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                        <Link
                          to={`/marketing-analytics/people?from=${dateFrom}&to=${dateTo}`}
                          className="inline-flex items-center gap-1 text-[11px] text-blue-300 hover:underline mt-2"
                        >
                          Manager scorecards →
                        </Link>
                      </div>
                    </div>
                    );
                  })()}
                </div>
              );
            })
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Mini bar — fixed-width visual indicator of one 0-100 metric.
const MiniBar = ({ label, score }) => {
  const pct = Math.max(2, Math.min(100, Number(score) || 0));
  const bgCls = score == null
    ? "bg-zinc-700/40"
    : score >= 85 ? "bg-emerald-500"
    : score >= 65 ? "bg-cyan-500"
    : score >= 45 ? "bg-zinc-500"
    : score >= 25 ? "bg-amber-500"
    : "bg-red-500";
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-[10px] text-zinc-500 w-[68px] shrink-0 uppercase tracking-wider truncate">
        {label}
      </span>
      <div className="flex-1 h-1.5 bg-zinc-800 rounded-full overflow-hidden min-w-[40px]">
        <div className={`h-full ${bgCls} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] text-zinc-300 w-6 shrink-0 text-right font-mono">{score ?? "—"}</span>
    </div>
  );
};

// Coach takeaway one-liner. Prefer disqualification_reason for junk/weak,
// otherwise first coaching_feedback bullet, otherwise summary_ru truncated.
function _coachTakeaway(lead) {
  if (lead.disqualification_reason) {
    return { kind: "junk", text: lead.disqualification_reason };
  }
  const cf = lead.coaching_feedback || [];
  if (cf.length > 0) {
    return { kind: "coach", text: cf[0] };
  }
  if (lead.summary_ru) {
    return { kind: "summary", text: String(lead.summary_ru).slice(0, 200) };
  }
  return { kind: "none", text: "" };
}

const CoachCard = ({ lead, isActive, onClick }) => {
  const tier = lead.quality_tier;
  const scorecard = lead.manager_scorecard || {};
  const takeaway = _coachTakeaway(lead);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-lg border transition-all p-3 ${
        isActive
          ? "bg-blue-500/10 border-blue-500/50 ring-1 ring-blue-500/30"
          : "bg-zinc-950/40 border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900/70"
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-[11px] font-semibold ${_avatarBgFromName(lead.deal_name || lead.owner?.name)}`}>
          {_initials(lead.deal_name)}
        </div>

        {/* Identity + meta */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-zinc-100 font-medium truncate text-sm">
              {lead.deal_name || "(unnamed deal)"}
            </span>
            {lead.amount_usd > 0 && (
              <span className="text-emerald-300 font-mono text-[12px] shrink-0">
                ${lead.amount_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-[11px] text-zinc-500 flex-wrap mt-0.5">
            {lead.owner?.name && (
              <span className="inline-flex items-center gap-1">
                <Users className="w-2.5 h-2.5" /> {lead.owner.name}
              </span>
            )}
            {lead.stage_label && (
              <span className={`px-1.5 py-0 rounded text-[10px] border ${stageBadgeFor(lead.stage_status, null)}`}>
                {lead.stage_label}
              </span>
            )}
            {lead.campaign && (
              <span className="truncate max-w-[260px]" title={lead.campaign}>
                · {lead.campaign}
              </span>
            )}
          </div>
        </div>

        {/* Right side: tier + overall score */}
        <div className="flex items-center gap-1.5 shrink-0">
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border ${tierClsFor(tier)}`}>
            <span className="font-semibold">{lead.quality_score ?? "—"}</span>
            <span className="opacity-80">·</span>
            <span>{tierLabelFor(tier)}</span>
          </span>
          {lead.overall_manager_score != null && (
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded border ${scoreBgClass(lead.overall_manager_score)}`}>
              <Users className="w-2.5 h-2.5" /> {lead.overall_manager_score}
            </span>
          )}
        </div>
      </div>

      {/* Mini scorecard (4 key axes) — only if at least one is scored. */}
      {CARD_METRICS.some((m) => (scorecard[m.key]?.score ?? null) != null) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-1 mt-2 ml-12">
          {CARD_METRICS.map((m) => (
            <MiniBar key={m.key} label={m.label} score={scorecard[m.key]?.score ?? null} />
          ))}
        </div>
      )}

      {/* Coach takeaway line */}
      {takeaway.text && (
        <div className={`mt-2 ml-12 text-[12px] leading-snug flex items-start gap-1.5 ${
          takeaway.kind === "junk"  ? "text-red-300" :
          takeaway.kind === "coach" ? "text-violet-200" :
          "text-zinc-400"
        }`}>
          <span className="shrink-0 mt-0.5">
            {takeaway.kind === "junk"  ? <ThumbsDown className="w-3 h-3 text-red-400" /> :
             takeaway.kind === "coach" ? <Crown className="w-3 h-3 text-violet-400" /> :
             <Info className="w-3 h-3 text-zinc-500" />}
          </span>
          <span className="line-clamp-2">{takeaway.text}</span>
        </div>
      )}
    </button>
  );
};

// CoachDrawer — full v7 coach analysis in a right-side slide-in panel.
// Keyboard: Esc closes, ←/→ navigates prev/next.
const CoachDrawer = ({ lead, position, total, onPrev, onNext, onClose, onRegrade, coachView = "manager" }) => {
  // Fullscreen toggle — for power-users digging into rich lead context.
  // Default is comfortable 800px (was 640px); user can press F or click
  // the fullscreen button to expand to full viewport width.
  const [fullscreen, setFullscreen] = React.useState(false);
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && onPrev) onPrev();
      if (e.key === "ArrowRight" && onNext) onNext();
      if (e.key === "f" || e.key === "F") setFullscreen((v) => !v);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50"
      onClick={onClose}
    >
      <div
        className={`absolute top-0 right-0 bottom-0 bg-zinc-950 border-l border-zinc-800 shadow-2xl flex flex-col transition-all ${
          fullscreen ? "left-0 w-full" : "w-full md:w-[820px]"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer header — sticky */}
        <div className="shrink-0 p-4 border-b border-zinc-800 flex items-start gap-3">
          <div className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-[12px] font-semibold ${_avatarBgFromName(lead.deal_name)}`}>
            {_initials(lead.deal_name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded border ${tierClsFor(lead.quality_tier)}`}>
                <span className="font-semibold">{lead.quality_score ?? "—"}</span>
                <span className="opacity-80">·</span>
                <span>{tierLabelFor(lead.quality_tier)}</span>
              </span>
              {lead.deal_crm_id ? (
                <a href={crmUrl("deal", lead.deal_crm_id)} target="_blank" rel="noreferrer" className="text-zinc-100 font-medium hover:text-blue-300 hover:underline truncate">
                  {lead.deal_name || "(unnamed deal)"}
                </a>
              ) : (
                <span className="text-zinc-100 font-medium truncate">{lead.deal_name || "(unnamed deal)"}</span>
              )}
              {lead.amount_usd > 0 && (
                <span className="text-emerald-300 font-mono shrink-0">
                  ${lead.amount_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </span>
              )}
            </div>
            <div className="text-zinc-500 text-xs mt-1 flex items-center gap-2 flex-wrap">
              {lead.owner?.name && <span>Owner: <span className="text-zinc-300">{lead.owner.name}</span></span>}
              {lead.stage_label && (
                <span className={`px-1.5 py-0 rounded text-[10px] border ${stageBadgeFor(lead.stage_status, null)}`}>
                  {lead.stage_label}
                </span>
              )}
              {lead.campaign && (
                <span className="truncate">· <Megaphone className="w-3 h-3 inline" /> {lead.campaign}</span>
              )}
            </div>
            {/* v9 attribution chip — tells reviewer at a glance WHO is
                responsible for the current state. Critical so a manager
                isn't unfairly judged when the lead simply never responded. */}
            {(lead.attribution || lead.unreachable_lead || lead.lead_engagement_score != null) && (
              <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[11px]">
                {lead.attribution && (() => {
                  const meta = {
                    manager:   { label: "Manager responsibility",   cls: "bg-amber-500/15 text-amber-200 border-amber-500/40" },
                    lead:      { label: "Lead responsibility",      cls: "bg-zinc-700/40 text-zinc-300 border-zinc-600/40" },
                    marketing: { label: "Marketing responsibility", cls: "bg-purple-500/15 text-purple-200 border-purple-500/40" },
                    mixed:     { label: "Mixed responsibility",     cls: "bg-zinc-700/40 text-zinc-300 border-zinc-600/40" },
                  }[lead.attribution] || { label: lead.attribution, cls: "bg-zinc-700/40 text-zinc-300 border-zinc-600/40" };
                  return (
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${meta.cls}`} title={lead.attribution_note}>
                      {meta.label}
                    </span>
                  );
                })()}
                {lead.unreachable_lead && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-zinc-700/40 text-zinc-300 border-zinc-600/40" title="Manager made multiple attempts but the lead never responded. Manager isn't at fault.">
                    🔇 Unreachable lead
                  </span>
                )}
                {lead.lead_responded === false && !lead.unreachable_lead && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border bg-zinc-700/40 text-zinc-300 border-zinc-600/40">
                    No response from lead yet
                  </span>
                )}
                {lead.lead_engagement_score != null && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] ${scoreBgClass(lead.lead_engagement_score)}`} title="Lead engagement — quality of the lead itself, separate from manager work">
                    Lead engagement {lead.lead_engagement_score}
                  </span>
                )}
                {lead.attribution_note && (
                  <span className="text-zinc-500 text-[11px] italic truncate max-w-md">
                    {lead.attribution_note}
                  </span>
                )}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-200 text-xl leading-none shrink-0" title="Close (Esc)">×</button>
        </div>

        {/* Drawer nav strip */}
        <div className="shrink-0 border-b border-zinc-800 px-4 py-2 flex items-center justify-between text-xs">
          <div className="text-zinc-500 flex items-center gap-2">
            <span>Lead <span className="text-zinc-300 font-medium">{position}</span> of {total}</span>
            {/* Cache state indicator + per-lead re-grade. Fresh leads can
                still be re-graded but it costs a Claude call. */}
            {lead.is_pending ? (
              <span className="text-zinc-500 inline-flex items-center gap-1">
                · <span className="text-amber-300">not graded</span>
              </span>
            ) : lead.cache_fresh === false ? (
              <span className="text-zinc-500 inline-flex items-center gap-1">
                · <span className="text-amber-300">stale grade</span>
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onRegrade && onRegrade(lead)}
              disabled={!onRegrade || lead._regrading}
              className={`px-2 py-1 rounded border inline-flex items-center gap-1 ${
                lead.is_pending || lead.cache_fresh === false
                  ? "border-amber-700/50 bg-amber-500/10 hover:bg-amber-500/15 text-amber-300"
                  : "border-zinc-700 hover:bg-zinc-800 text-zinc-300"
              } disabled:opacity-40 disabled:cursor-not-allowed`}
              title={lead.is_pending
                ? "Grade this lead now — populates the full coach report"
                : lead.cache_fresh === false
                  ? "Re-grade with the latest prompt — unlocks raw activity timeline"
                  : "Re-grade — fresh Claude call (~$0.05-0.15)"
              }
            >
              {lead._regrading ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Grading…</>
              ) : (
                <><Wand2 className="w-3.5 h-3.5" /> {lead.is_pending ? "Grade now" : "Re-grade"}</>
              )}
            </button>
            <span className="text-zinc-700 mx-1">|</span>
            <button
              onClick={() => setFullscreen((v) => !v)}
              className="px-2 py-1 rounded border border-zinc-700 hover:bg-zinc-800 inline-flex items-center gap-1 text-zinc-300"
              title={`${fullscreen ? "Exit fullscreen" : "Fullscreen"} (F)`}
            >
              {fullscreen ? "Exit fullscreen" : "Fullscreen"}
            </button>
            <span className="text-zinc-700 mx-1">|</span>
            <button
              onClick={onPrev}
              disabled={!onPrev}
              className="px-2 py-1 rounded border border-zinc-700 hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
              title="Previous lead (←)"
            >
              <ChevronRight className="w-3.5 h-3.5 rotate-180" />
              Prev
            </button>
            <button
              onClick={onNext}
              disabled={!onNext}
              className="px-2 py-1 rounded border border-zinc-700 hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
              title="Next lead (→)"
            >
              Next
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-4">
          <AiQualityPanel
            summary={lead}
            dealId={null}
            isSummarizing={false}
            onGenerate={() => {}}
            coachView={coachView}
          />
        </div>
      </div>
    </div>
  );
};

// LeadDetailModal — what actually happened in this deal.
//
// It used to render the AI quality panel: a junk score, "marketing signals",
// a "why not buying" counter reading «не дозвонились × 0», a raw activity dump
// and key facts still saying "лид создан в HubSpot". All of it was either
// stale after the CRM migration or an opinion the CRM already answers better.
// What a person needs here is the request itself and the deal's own history,
// so that is what this shows. Marker: lead-modal-facts-v1
// Where the lead came from on the landing: a filled form, a chat conversation,
// or a booking. The CRM records it as `lead_type`; without it every lead on the
// page looks the same, and a chat lead is a different animal from a form one.
// Marker: lead-type-badge-v1
const LEAD_TYPE_META = {
  FORM_LEAD: { label: "form", icon: ClipboardList, cls: "bg-sky-500/15 border-sky-500/40 text-sky-200", title: "Filled the form on the landing" },
  CHAT_LEAD: { label: "chat", icon: MessageSquare, cls: "bg-violet-500/15 border-violet-500/40 text-violet-200", title: "Came through the landing chat" },
  BOOKING: { label: "booking", icon: CalendarClock, cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200", title: "Booked a call on the landing" },
  BOOKING_LEAD: { label: "booking", icon: CalendarClock, cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200", title: "Booked a call on the landing" },
};

export const LeadTypeBadge = ({ leadType, dealName }) => {
  // Older deals carry no lead_type column — the landing still stamped the type
  // into the deal title, so read it from there rather than showing nothing.
  const fromTitle = !leadType && dealName
    ? (String(dealName).match(/\[(FORM_LEAD|CHAT_LEAD|BOOKING_LEAD|BOOKING)\]/i) || [])[1]
    : null;
  const key = String(leadType || fromTitle || "").toUpperCase();
  const meta = LEAD_TYPE_META[key];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1 py-0 rounded text-[9px] uppercase tracking-wider border ${meta.cls}`}
      title={meta.title}
    >
      <Icon className="w-2.5 h-2.5" />
      {meta.label}
    </span>
  );
};

export const LeadDetailModal = ({ lead, onClose }) => {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const deal = lead?.deal || {};
  const contact = lead?.contact || null;
  const owner = lead?.owner || {};

  const verdictCls = {
    qualified: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300",
    trash: "bg-red-500/15 border-red-500/40 text-red-300",
  }[lead?.bucket] || "bg-zinc-800 border-zinc-700 text-zinc-300";
  const verdictLabel = {
    qualified: "Qualified",
    trash: "Trash",
  }[lead?.bucket] || "Not judged yet";

  // Facts the client typed into the landing form. Empty ones are dropped
  // rather than rendered as "—" rows: a wall of dashes reads as broken.
  const facts = [
    ["Company", deal.company_name || contact?.company],
    ["Project size", deal.company_size],
    ["Geo", [deal.city, deal.country].filter(Boolean).join(", ")],
    ["Landing", deal.site],
    ["Lead type", deal.lead_type],
    ["Creative", deal.creative],
    ["Contact", contact?.name],
    ["E-mail", contact?.email],
    ["Phone", contact?.phone],
  ].filter(([, v]) => v);

  const money = deal.amount_usd > 0
    ? `$${Number(deal.amount_usd).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : null;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-zinc-900 border border-zinc-700 rounded-lg max-w-3xl w-full my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-zinc-800 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center px-2 py-0.5 text-[11px] rounded border ${verdictCls}`}>
                {verdictLabel}
              </span>
              {deal.id ? (
                <a
                  href={crmUrl("deal", deal.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-zinc-100 font-medium hover:text-blue-300 hover:underline inline-flex items-center gap-1"
                >
                  {deal.name || "(unnamed deal)"}
                  <ExternalLink className="w-3 h-3 opacity-60" />
                </a>
              ) : (
                <span className="text-zinc-100 font-medium">{deal.name || "(unnamed deal)"}</span>
              )}
              <LeadTypeBadge leadType={deal.lead_type} dealName={deal.name} />
              {money && <span className="text-emerald-300 font-mono">{money}</span>}
            </div>
            <div className="text-zinc-500 text-xs mt-1 flex items-center gap-2 flex-wrap">
              <Megaphone className="w-3 h-3" /> {lead?.campaign || "Untagged"}
              {deal.stage_label && (
                <span className={`px-1.5 py-0 rounded text-[10px] border ${stageBadgeFor(deal.stage_status, null)}`}>
                  {deal.stage_label}
                </span>
              )}
              {owner?.name && <span>· Owner: {owner.name}</span>}
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300 text-2xl leading-none">×</button>
        </div>

        <div className="p-4 space-y-4">
          {/* Why it sits in this bucket — the CRM's answer, verbatim. */}
          {lead?.verdict_reason && (
            <div className="text-[11px] text-zinc-400 bg-zinc-950/40 border border-zinc-800 rounded px-3 py-2">
              {lead.verdict_reason}
              {deal.loss_reason && (
                <div className="text-zinc-500 mt-1">Loss reason in the CRM: {deal.loss_reason}</div>
              )}
            </div>
          )}

          {facts.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
                What the lead asked for
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[12px]">
                {facts.map(([label, value]) => (
                  <div key={label} className="flex gap-2 min-w-0">
                    <span className="text-zinc-500 shrink-0 w-24">{label}</span>
                    <span className="text-zinc-200 truncate" title={String(value)}>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stage history + the CRM's call gradings, straight from the CRM. */}
          {deal.id && <DealActivityTimeline dealId={deal.id} />}
        </div>
      </div>
    </div>
  );
};

const LeadList = ({ campaign, leads, isLoading, summarizingDeals, onGenerate }) => {
  if (isLoading && !leads) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 text-xs px-4 py-3">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Loading leads…
      </div>
    );
  }
  if (!leads || leads.length === 0) {
    return (
      <div className="px-4 py-3 text-xs text-zinc-500">
        No leads in this date range for <code>{campaign}</code>.
      </div>
    );
  }
  return (
    <div className="px-4 py-3 space-y-2">
      <div className="text-[11px] uppercase text-zinc-500 tracking-wider mb-1">
        {leads.length} {leads.length === 1 ? "lead" : "leads"}
      </div>
      {leads.map((lead, idx) => {
        const c = lead.contact || {};
        const d = lead.deal;
        const owner = lead.owner || (d && d.owner);
        const stage = d?.stage_status;
        const stageLabel = d?.stage_label;
        const stageProb = d?.stage_probability;
        const stageCls = stage ? stageBadgeFor(stage, stageProb) : null;
        const summary = lead.ai_summary;
        const dealId = d?.id;
        const isSummarizing = dealId && summarizingDeals.has(dealId);
        return (
          <div
            key={c.crm_id || idx}
            className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 text-xs space-y-2"
          >
            {/* Top line: name + stage + amount */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                {c.crm_id ? (
                  <a
                    href={crmUrl("contact", c.crm_id)}
                    target="_blank"
                    rel="noreferrer"
                    title="Open contact in the CRM"
                    className="text-zinc-100 font-medium hover:text-blue-300 hover:underline inline-flex items-center gap-1"
                  >
                    {c.name || "(no name)"}
                    <ExternalLink className="w-3 h-3 text-zinc-500" />
                  </a>
                ) : (
                  <span className="text-zinc-100 font-medium">{c.name || "(no name)"}</span>
                )}
                {stageCls && (
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 text-[10px] rounded border ${stageCls}`}
                    title={
                      stageProb != null
                        ? `CRM stage · win probability ${(Number(stageProb) * 100).toFixed(0)}%${d?.pipeline_label ? ` · pipeline: ${d.pipeline_label}` : ""}`
                        : "CRM stage"
                    }
                  >
                    {stageLabel || (stage ? STAGE_BADGES[stage]?.label : "—")}
                  </span>
                )}
                {c.country && (
                  <span className="inline-flex items-center gap-1 text-zinc-500">
                    <MapPin className="w-3 h-3" />
                    {c.city ? `${c.city}, ${c.country}` : c.country}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                {d && d.amount_usd > 0 && (
                  <span className="inline-flex items-center gap-1.5">
                    {d.crm_id ? (
                      <a
                        href={crmUrl("deal", d.crm_id)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-emerald-300 font-mono hover:text-emerald-200 hover:underline"
                        title="Open deal in the CRM"
                      >
                        ${d.amount_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </a>
                    ) : (
                      <span className="text-emerald-300 font-mono">
                        ${d.amount_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </span>
                    )}
                    {d.currency !== "USD" && (
                      <span className="text-zinc-500">
                        ({d.currency} {d.amount_native.toLocaleString(undefined, { maximumFractionDigits: 0 })})
                      </span>
                    )}
                  </span>
                )}
                {owner && <span className="text-zinc-400">{owner.name}</span>}
              </div>
            </div>

            {/* Contact row */}
            <div className="flex items-center gap-3 text-zinc-500 flex-wrap">
              {c.email && (
                <span className="inline-flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  <a href={`mailto:${c.email}`} className="hover:text-zinc-300">{c.email}</a>
                </span>
              )}
              {c.phone && (
                <span className="inline-flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {c.phone}
                </span>
              )}
              {c.created_at && (
                <span className="text-zinc-600">created {String(c.created_at).slice(0, 10)}</span>
              )}
            </div>

            {/* Extra deals attached to this same contact — explains "deals > leads"
                rows. Each shown with its own stage so the user can see the spread. */}
            {Array.isArray(d?.extras) && d.extras.length > 0 && (
              <div className="bg-zinc-900/40 border border-zinc-800/70 rounded px-2 py-1.5 space-y-1">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                  +{d.extras.length} other deal{d.extras.length > 1 ? "s" : ""} on this contact
                </div>
                {d.extras.map((ed, j) => {
                  const cls = stageBadgeFor(ed.stage_status, null);
                  return (
                    <div key={ed.id || j} className="flex items-center gap-2 flex-wrap text-[11px]">
                      <span className={`inline-flex items-center px-1.5 py-0 text-[10px] rounded border ${cls}`}>
                        {ed.stage_label || ed.stage_status}
                      </span>
                      {ed.crm_id ? (
                        <a href={crmUrl("deal", ed.crm_id)} target="_blank" rel="noreferrer" className="text-zinc-200 hover:text-blue-300 hover:underline">
                          {ed.name || "(unnamed deal)"}
                        </a>
                      ) : (
                        <span className="text-zinc-200">{ed.name || "(unnamed deal)"}</span>
                      )}
                      {ed.amount_usd > 0 && (
                        <span className="text-emerald-300 font-mono">${ed.amount_usd.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                      )}
                      {ed.created_at && (
                        <span className="text-zinc-600">{String(ed.created_at).slice(0, 10)}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Loss/won reason */}
            {d?.lost_reason && (
              <div className="bg-red-500/5 border border-red-500/20 rounded px-2 py-1 text-red-300">
                <span className="text-red-400/70 mr-1">Lost reason:</span> {d.lost_reason}
              </div>
            )}
            {d?.won_reason && (
              <div className="bg-emerald-500/5 border border-emerald-500/20 rounded px-2 py-1 text-emerald-300">
                <span className="text-emerald-400/70 mr-1">Won reason:</span> {d.won_reason}
              </div>
            )}

            {/* AI quality analysis block — structured fields when v2 summary
                is available, plain prompt when not. Russian summary text is
                what the LLM emits; chips/labels are English UI chrome. */}
            <AiQualityPanel
              summary={summary}
              dealId={dealId}
              isSummarizing={isSummarizing}
              onGenerate={onGenerate}
            />

            {/* Description excerpt — collapsed by default behind summary; show as small print */}
            {d?.description_excerpt && (
              <details className="text-zinc-500">
                <summary className="cursor-pointer hover:text-zinc-400 text-[10px] uppercase tracking-wider">
                  Raw lead description
                </summary>
                <pre className="mt-1 whitespace-pre-wrap font-mono text-[11px] text-zinc-400">{d.description_excerpt}</pre>
              </details>
            )}
          </div>
        );
      })}
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, sublabel, color = "zinc" }) => (
  <Card className={`bg-zinc-900/50 border-zinc-800`}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-zinc-500 uppercase tracking-wider">{label}</span>
        <Icon className={`w-4 h-4 text-${color}-400`} />
      </div>
      <div className="text-2xl font-semibold text-zinc-50">{value}</div>
      {sublabel && <div className="text-xs text-zinc-500 mt-1">{sublabel}</div>}
    </CardContent>
  </Card>
);

export default function MarketingAnalyticsPage() {
  const { user } = useAuth();
  // Access: role-based OR marketing-agency allowlist. /auth/me + /auth/login
  // now return `marketing_agency_user` so we don't need a second API call.
  // Marker: marketing-agency-page-gate-v1
  const allowed = ALLOWED_ROLES.has(user?.role) || user?.marketing_agency_user === true;

  // Default to THIS calendar month so the page opens showing what's
  // happening right now, not what already happened. Stepping back to last
  // month is one click (or use the ‹ arrow). Previously defaulted to last
  // month per v12 (2026-05-07), reverted 2026-05-19 because users open the
  // page mid-month to monitor live campaigns, not to retro-analyze.
  const _thisMonth = calendarMonthRange(0);
  const [dateFrom, setDateFrom] = useState(_thisMonth.from);
  const [dateTo, setDateTo] = useState(_thisMonth.to);
  // monthAnchor lets the user step backward/forward through calendar months.
  // 0 = this month (default), 1 = last month, 2 = month-before-last, etc.
  // null = custom range (date pickers control directly, no month anchor).
  const [monthAnchor, setMonthAnchor] = useState(0);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState("spend_usd");
  const [sortDir, setSortDir] = useState("desc");

  // Drill-down state — campaign row → leads list, lazily fetched per row.
  const [expanded, setExpanded] = useState(() => new Set());
  const [leadsByCampaign, setLeadsByCampaign] = useState({});
  const [loadingCampaigns, setLoadingCampaigns] = useState(() => new Set());
  const [summarizingDeals, setSummarizingDeals] = useState(() => new Set());

  // Lazy-loader for per-campaign lead list. Shared between the binary
  // qualified section (top of page) and the leaderboard's per-campaign
  // expand below — both pull the same cache so we never double-fetch.
  const ensureCampaignLeads = async (campaign) => {
    if (leadsByCampaign[campaign]) return;
    setLoadingCampaigns((prev) => new Set(prev).add(campaign));
    try {
      const leadsRes = await api.get("/marketing-analytics/campaign-leads", {
        params: { campaign, date_from: dateFrom, date_to: dateTo },
      });
      setLeadsByCampaign((prev) => ({ ...prev, [campaign]: leadsRes.data?.leads || [] }));
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not load lead list";
      toast.error(detail);
    } finally {
      setLoadingCampaigns((prev) => {
        const n = new Set(prev);
        n.delete(campaign);
        return n;
      });
    }
  };

  // Deal-centric lead list for the binary qualified section.
  //
  // The cache is keyed by campaign AND date range. Keyed by campaign alone it
  // survived a period change: the chips re-fetched for the new range while the
  // expanded list kept rows from the old one, so a "17 qualified" chip sat
  // above a list of 3 — the previous period's leads. Marker: lead-cache-range-v1
  const [binaryLeadsByCampaign, setBinaryLeadsByCampaign] = useState({});
  const [loadingBinaryCampaigns, setLoadingBinaryCampaigns] = useState(() => new Set());

  // Drop the cache whenever the range moves; nothing in it is valid any more.
  useEffect(() => {
    setBinaryLeadsByCampaign({});
  }, [dateFrom, dateTo]);

  const ensureBinaryCampaignLeads = async (campaign) => {
    if (binaryLeadsByCampaign[campaign]) return;
    setLoadingBinaryCampaigns((prev) => new Set(prev).add(campaign));
    try {
      const res = await api.get("/marketing-analytics/campaign-binary-leads", {
        params: { campaign, date_from: dateFrom, date_to: dateTo },
      });
      setBinaryLeadsByCampaign((prev) => ({ ...prev, [campaign]: res.data?.leads || [] }));
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not load lead list";
      toast.error(detail);
    } finally {
      setLoadingBinaryCampaigns((prev) => {
        const n = new Set(prev);
        n.delete(campaign);
        return n;
      });
    }
  };

  // Selected lead → opens LeadDetailModal with full AI analysis. Flattened
  // from {deal, contact, owner, ai_summary} into the shape the modal expects
  // (top-level AI fields + deal_name/amount_usd/campaign/stage_label/owner).
  const [openLeadFlat, setOpenLeadFlat] = useState(null);
  const handleOpenLeadFromBinary = (leadRow, campaignName) => {
    if (!leadRow) return;
    // The modal now shows what is IN the deal, so it gets the deal, the
    // contact and the verdict — not a flattened AI payload.
    setOpenLeadFlat({
      deal: leadRow.deal || {},
      contact: leadRow.contact || null,
      owner: leadRow.owner || {},
      bucket: leadRow.bucket,
      verdict_source: leadRow.verdict_source,
      verdict_reason: leadRow.verdict_reason,
      campaign: campaignName,
    });
  };

  const toggleExpand = async (campaign) => {
    const next = new Set(expanded);
    if (next.has(campaign)) {
      next.delete(campaign);
      setExpanded(next);
      return;
    }
    next.add(campaign);
    setExpanded(next);
    // Lazy-fetch BOTH the lead list and the deep-dive aggregation
    // (funnel + manager × stage + cached verdict if any).
    if (!leadsByCampaign[campaign]) {
      setLoadingCampaigns((prev) => new Set(prev).add(campaign));
      try {
        const [leadsRes, ddRes] = await Promise.all([
          api.get("/marketing-analytics/campaign-leads", {
            params: { campaign, date_from: dateFrom, date_to: dateTo },
          }),
          api.get("/marketing-analytics/campaign-deep-dive", {
            params: { campaign, date_from: dateFrom, date_to: dateTo },
          }),
        ]);
        setLeadsByCampaign((prev) => ({ ...prev, [campaign]: leadsRes.data?.leads || [] }));
        setDeepDiveByCampaign((prev) => ({ ...prev, [campaign]: ddRes.data || null }));
      } catch (e) {
        const detail = e?.response?.data?.detail || "Could not load campaign data";
        toast.error(detail);
      } finally {
        setLoadingCampaigns((prev) => {
          const n = new Set(prev);
          n.delete(campaign);
          return n;
        });
      }
    } else if (!deepDiveByCampaign[campaign]) {
      // Leads were fetched earlier (e.g. via bulkSummarize) but deep-dive wasn't.
      try {
        const ddRes = await api.get("/marketing-analytics/campaign-deep-dive", {
          params: { campaign, date_from: dateFrom, date_to: dateTo },
        });
        setDeepDiveByCampaign((prev) => ({ ...prev, [campaign]: ddRes.data || null }));
      } catch (_) { /* non-critical */ }
    }
  };

  const generateCampaignVerdict = async (campaign, force = false) => {
    setGeneratingVerdict((prev) => new Set(prev).add(campaign));
    try {
      const res = await api.post("/marketing-analytics/campaign-verdict", null, {
        params: { campaign, date_from: dateFrom, date_to: dateTo, force },
      });
      // Backend returns {...verdict, data: agg} — store both into deep-dive cache.
      setDeepDiveByCampaign((prev) => ({
        ...prev,
        [campaign]: { ...(res.data?.data || {}), verdict: res.data },
      }));
      toast.success(`AI verdict generated for ${campaign}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not generate verdict";
      toast.error(detail);
    } finally {
      setGeneratingVerdict((prev) => {
        const n = new Set(prev);
        n.delete(campaign);
        return n;
      });
    }
  };

  const generateAllCampaignVerdicts = async () => {
    setBulkVerdictRunning(true);
    try {
      const res = await api.post("/marketing-analytics/campaign-verdicts/bulk", null, {
        params: { date_from: dateFrom, date_to: dateTo, max_campaigns: 20 },
      });
      const { generated, skipped_cached, errors, considered, capped } = res.data || {};
      const errLine = errors?.length ? ` · ${errors.length} error${errors.length > 1 ? "s" : ""}` : "";
      const capLine = capped ? ` (capped at 20 campaigns)` : "";
      toast.success(`Graded ${generated} new · ${skipped_cached} cached${errLine}${capLine}`);
      // Refresh overview so the new verdict grades appear in the table chips.
      // Drop the deep-dive cache so expanded rows re-fetch with fresh verdicts.
      setDeepDiveByCampaign({});
      const ovRes = await api.get("/marketing-analytics/overview", {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setData(ovRes.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Bulk verdict generation failed";
      toast.error(detail);
    } finally {
      setBulkVerdictRunning(false);
    }
  };

  const generateSummary = async (campaign, dealId, force = false) => {
    if (!dealId) return;
    setSummarizingDeals((prev) => new Set(prev).add(dealId));
    try {
      const res = await api.post(`/marketing-analytics/leads/${dealId}/summarize`, null, {
        params: force ? { force: true } : {},
      });
      const summary = {
        text: res.data?.summary,
        generated_at: res.data?.generated_at,
        model: res.data?.model,
      };
      setLeadsByCampaign((prev) => {
        const next = { ...prev };
        const list = next[campaign] || [];
        next[campaign] = list.map((lead) =>
          lead?.deal?.id === dealId ? { ...lead, ai_summary: summary } : lead,
        );
        return next;
      });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not generate summary";
      toast.error(detail);
    } finally {
      setSummarizingDeals((prev) => {
        const n = new Set(prev);
        n.delete(dealId);
        return n;
      });
    }
  };

  const [pullingMeta, setPullingMeta] = useState(false);
  const [bulkSummarizing, setBulkSummarizing] = useState(() => new Set());
  // Deep-dive (funnel + manager + verdict) per campaign — lazy-loaded when row expands.
  const [deepDiveByCampaign, setDeepDiveByCampaign] = useState({});
  const [generatingVerdict, setGeneratingVerdict] = useState(() => new Set());
  // Bulk verdict generator (loops through all campaigns) — separate state from
  // per-campaign generatingVerdict so the leaderboard button can show its own spinner.
  const [bulkVerdictRunning, setBulkVerdictRunning] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);
  // Filter state for the unified leads table — tier chip filter + manager + sort key.
  const [leadsTierFilter, setLeadsTierFilter] = useState(null);  // null = all
  const [leadsOwnerFilter, setLeadsOwnerFilter] = useState("");  // "" = all
  // Default: newest leads first. Most workflows are "what came in recently",
  // not "what's our best lead ever". Score-sort is still in the dropdown.
  const [leadsSortKey, setLeadsSortKey] = useState("date_desc");
  // Hard-filter by explicit deal_id set — set by Priority Action card
  // clicks. Tier filter alone is too narrow because "Hot leads" / "Stuck
  // deals" / "Coaching gap" span multiple tiers.
  const [leadsHardFilter, setLeadsHardFilter] = useState(null);  // {ids: Set, label, tone} | null
  // Coach view toggle: "manager" focuses on manager effort/coaching;
  // "lead_quality" focuses on lead quality and marketing source. Same data,
  // two lenses (see Decision 2026-05-18 #2).
  const [coachView, setCoachView] = useState("manager");
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  const [leaderboardLead, setLeaderboardLead] = useState(null);
  const [leaderboardLeadIndex, setLeaderboardLeadIndex] = useState(0);
  // Bulk-summarize job state (background task pattern). Set when Re-analyze
  // all starts; polled every 2s while status==="running" so the user sees
  // progress without hitting nginx's 60s upstream timeout (LLM + 37 leads
  // sequential = 18 minutes).
  const [summarizeJob, setSummarizeJob] = useState(null);
  // Manager scorecard summary — per-manager averages across the date range.
  // Cheap (read-only over marketing_lead_summaries cache); auto-fetched with leaderboard.
  const [managerSummary, setManagerSummary] = useState(null);

  const fetchOverview = async () => {
    setLoading(true);
    setError(null);
    // Drill-down caches are date-range-specific — drop them when reloading.
    setExpanded(new Set());
    setLeadsByCampaign({});
    setDeepDiveByCampaign({});
    try {
      const res = await api.get("/marketing-analytics/overview", {
        params: { date_from: dateFrom, date_to: dateTo },
      });
      setData(res.data);
      // Refresh leaderboard for the new date range too — keep them in sync.
      fetchLeaderboard();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Failed to load analytics";
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const pullMetaNow = async () => {
    setPullingMeta(true);
    try {
      await api.post("/meta-ads/sync/incremental");
      toast.success("Meta Ads sync triggered — refreshing analytics");
      await fetchOverview();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Could not trigger Meta sync";
      toast.error(detail);
    } finally {
      setPullingMeta(false);
    }
  };

  // Poll a bulk-summarize job until it reaches a terminal state. Returns
  // the final job snapshot. Resilient to transient errors (network blips,
  // single 5xx, brief 404 right after backend start) — retries up to 5
  // consecutive failures before giving up. Honours `orphaned` status
  // (persisted in Mongo but the worker process is gone) so the user gets a
  // useful error instead of a forever spinner.
  // Hardened 2026-05-19 (see Decision in CLAUDE.md).
  const pollUntilDone = async (jobId) => {
    let consecutiveErrors = 0;
    const MAX_ERRORS = 5;
    let lastRefresh = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      await new Promise((r) => setTimeout(r, 2000));
      let job;
      try {
        const r = await api.get(`/marketing-analytics/summarize-jobs/${jobId}`);
        job = r.data;
        consecutiveErrors = 0;
      } catch (e) {
        consecutiveErrors += 1;
        if (consecutiveErrors >= MAX_ERRORS) {
          const status = e?.response?.status;
          const detail = e?.response?.data?.detail || e.message;
          throw new Error(
            status === 404
              ? "Lost track of the analysis job. Refresh the page — completed leads are already cached."
              : `Stopped polling after ${MAX_ERRORS} consecutive failures (last: ${detail || status || "unknown"}).`,
          );
        }
        continue;
      }
      setSummarizeJob(job);
      const now = Date.now();
      if (now - lastRefresh > 10000 && (job.done || 0) > 0) {
        lastRefresh = now;
        fetchLeaderboard();  // best-effort progressive refresh
      }
      if (job.status === "done" || job.status === "error" || job.status === "orphaned") {
        return job;
      }
    }
  };

  // Single bulk-analyze entry point. Pass `campaign` for one campaign, or
  // omit it to walk every campaign in the date range. Posts to the new
  // query-param endpoint (the old path-param route returned 404 because
  // campaign names contain forward slashes).
  const runBulkSummarize = async ({ campaign, force = false, key: customKey, includeAllSources = false } = {}) => {
    // Custom key lets the two leaderboard buttons (Analyze new vs Re-analyze
    // all) show independent loading state. Falls back to legacy behavior.
    // includeAllSources=true switches backend to scan ALL owned deals
    // (including offline / manual / referral) instead of campaign walk.
    const key = customKey || campaign || "__ALL__";
    setBulkSummarizing((prev) => new Set(prev).add(key));
    try {
      const params = { date_from: dateFrom, date_to: dateTo, force };
      if (campaign) params.campaign = campaign;
      if (includeAllSources) params.include_all_sources = true;
      // v7: backend returns immediately with a job_id; we poll status.
      // Avoids the nginx 60s timeout that v6 hit with LLM + bulk.
      const startRes = await api.post("/marketing-analytics/summarize-all", null, { params });
      const jobId = startRes.data?.job_id;
      setSummarizeJob(startRes.data);

      if (!jobId) {
        toast.error("Could not start bulk analysis");
        return;
      }

      const finalJob = await pollUntilDone(jobId);
      // Surface terminal state honestly: orphaned & error → red toast; clean
      // success → green; partial (some errors) → yellow. The old code fired
      // a green toast even when every lead failed.
      const generated = finalJob.generated || 0;
      const skipped = finalJob.skipped_cached || 0;
      const errors = finalJob.errors || [];
      const total = finalJob.total || 0;
      if (finalJob.status === "orphaned") {
        toast.error(
          finalJob.orphan_reason ||
            "Backend restarted while the analysis was running. Click Analyze again to resume — completed leads are already cached.",
        );
      } else if (finalJob.status === "error") {
        toast.error(`Analysis crashed: ${finalJob.errors?.[0]?.error || "see backend logs"}`);
      } else if (errors.length === total && total > 0) {
        toast.error(
          `All ${total} leads failed. First error: ${errors[0]?.error || "unknown"}`,
        );
      } else if (errors.length > 0) {
        toast.warning(
          `Coach finished with errors: ${generated} graded · ${skipped} cached · ${errors.length} failed (${total} total)`,
        );
      } else {
        toast.success(
          `Coach finished: ${generated} graded · ${skipped} cached (${total} total)`,
        );
      }

      // Drop drill-down caches so re-expand pulls fresh data.
      if (campaign) {
        setLeadsByCampaign((prev) => {
          const next = { ...prev };
          delete next[campaign];
          return next;
        });
      } else {
        setLeadsByCampaign({});
      }
      const toRefetch = campaign ? [campaign] : Array.from(expanded);
      for (const cn of toRefetch) {
        if (expanded.has(cn)) {
          setLoadingCampaigns((p) => new Set(p).add(cn));
          try {
            const r = await api.get("/marketing-analytics/campaign-leads", {
              params: { campaign: cn, date_from: dateFrom, date_to: dateTo },
            });
            setLeadsByCampaign((prev) => ({ ...prev, [cn]: r.data?.leads || [] }));
          } finally {
            setLoadingCampaigns((p) => {
              const n = new Set(p);
              n.delete(cn);
              return n;
            });
          }
        }
      }
      await fetchOverview();
      await fetchLeaderboard();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Bulk analysis failed";
      toast.error(detail);
    } finally {
      setBulkSummarizing((prev) => {
        const n = new Set(prev);
        n.delete(key);
        return n;
      });
      // Clear job indicator after a short delay so user sees "done" briefly.
      setTimeout(() => setSummarizeJob(null), 3000);
    }
  };

  const fetchLeaderboard = async () => {
    setLeaderboardLoading(true);
    try {
      const [lb, ms] = await Promise.all([
        api.get("/marketing-analytics/quality-leaderboard", {
          params: { date_from: dateFrom, date_to: dateTo, limit_top: 10, limit_bottom: 10 },
        }),
        api.get("/marketing-analytics/manager-scorecard-summary", {
          params: { date_from: dateFrom, date_to: dateTo },
        }).catch(() => ({ data: null })),
      ]);
      setLeaderboard(lb.data);
      setManagerSummary(ms.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Could not load leaderboard";
      toast.error(detail);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  useEffect(() => {
    if (allowed) {
      fetchOverview();
      // Auto-load leaderboard on mount so user doesn't have to re-click
      // "Load leaderboard" each time they reopen the page. Cached summaries
      // already in Mongo render immediately; if none exist yet, the UI shows
      // the "click Analyze all leads" empty-state CTA.
      fetchLeaderboard();
      // Resume tracking any bulk-analyze job that's still running on the
      // backend — e.g. user clicked Analyze in another tab, or refreshed
      // while analysis was in flight. Without this, a long-running job is
      // invisible after navigation and the user has no idea what's happening.
      (async () => {
        try {
          const r = await api.get("/marketing-analytics/summarize-jobs", { params: { limit: 5 } });
          const runningJob = (r.data?.jobs || []).find((j) => j.status === "running");
          if (runningJob?.job_id) {
            // Pre-populate the progress card and resume polling. We
            // intentionally don't tie this into bulkSummarizing keys —
            // there's no original button context to attribute it to.
            setSummarizeJob(runningJob);
            try {
              const finalJob = await pollUntilDone(runningJob.job_id);
              if (finalJob.status === "orphaned") {
                toast.error(finalJob.orphan_reason || "Background analysis was interrupted by a backend restart.");
              } else if ((finalJob.errors || []).length) {
                toast.warning(
                  `Background analysis finished with ${(finalJob.errors || []).length} errors — see the progress card.`,
                );
              } else {
                toast.success("Background analysis finished.");
              }
              await fetchLeaderboard();
            } catch (e) {
              toast.error(e?.message || "Lost track of the background analysis.");
            } finally {
              setTimeout(() => setSummarizeJob(null), 3000);
            }
          }
        } catch (_) {
          // Best-effort — listing recent jobs is purely additive. If the
          // endpoint 404s on an older backend, we silently skip.
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sortedCampaigns = useMemo(() => {
    if (!data?.campaigns) return [];
    const arr = [...data.campaigns];
    arr.sort((a, b) => {
      const av = a[sortBy] ?? 0;
      const bv = b[sortBy] ?? 0;
      if (typeof av === "string") return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
    return arr;
  }, [data, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortBy(col);
      setSortDir("desc");
    }
  };

  const k = data?.kpis || {};
  const funnel = data?.funnel || [];
  const owners = data?.owners || [];
  const ownerTotals = data?.owner_totals || {};
  const ownerXCampaign = data?.owner_x_campaign || {};
  const dailyTrend = data?.daily_trend || [];

  // Owner × campaign matrix needs the union of campaigns that appear in the data.
  const matrixCampaigns = useMemo(() => {
    const set = new Set();
    Object.keys(ownerXCampaign).forEach((key) => {
      const camp = key.split("|").slice(1).join("|");
      set.add(camp);
    });
    const totals = {};
    Object.entries(ownerXCampaign).forEach(([key, v]) => {
      const camp = key.split("|").slice(1).join("|");
      totals[camp] = (totals[camp] || 0) + (v.leads || 0);
    });
    return Array.from(set).sort((a, b) => (totals[b] || 0) - (totals[a] || 0));
  }, [ownerXCampaign]);

  const dailyMax = useMemo(() => {
    return Math.max(1, ...dailyTrend.map((r) => r.spend || 0));
  }, [dailyTrend]);

  // Separate scale for CRM leads bar — typically 0-5 leads/day, much
  // smaller absolute number than spend, so we need its own axis.
  const dailyLeadsMax = useMemo(() => {
    return Math.max(1, ...dailyTrend.map((r) => r.leads_crm || 0));
  }, [dailyTrend]);

  if (!allowed) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-amber-400">
              <AlertTriangle className="w-5 h-5" />
              <span>This page is only available to admin, sales_director, crm_manager, sales, and pm_lead roles.</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Funnel bar widths: percentage of impressions (or top stage value).
  const funnelTop = funnel[0]?.count || 1;

  // Counts feeding the Analyze new / Re-analyze buttons (was inside Mission
  // Control IIFE before 2026-05-25 cut). Buttons stay essential as the only
  // way to refresh AI verdicts when new leads arrive.
  const totalLeadsInRange = leaderboard?.total ?? 0;
  const pendingCount  = leaderboard?.pending_count  ?? 0;
  const staleCount    = leaderboard?.stale_count    ?? 0;
  const newLeadsCount = pendingCount + staleCount;
  const isAnalyzing   = bulkSummarizing.has("__ALL__");
  const isReanalyzing = bulkSummarizing.has("__ALL_FORCE__");
  const isAllSources  = bulkSummarizing.has("__ALL_SOURCES__");

  return (
    <TooltipProvider delayDuration={150}>
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Tab nav — Overview + People + Sales scoring + Landings.
          Marketing-agency users (allowlist, role=team) see ONLY Overview;
          the other tabs link to internal coaching views that 403 for them
          anyway and clutter the agency's mental model.
          Marker: marketing-analytics-scope-v1 */}
      <div className="flex items-center gap-1 border-b border-zinc-800 pb-0.5 marketing-analytics-tabs">
        <Link
          to="/marketing-analytics"
          className="px-4 py-2 text-sm text-blue-400 font-medium border-b-2 border-blue-500 -mb-0.5"
        >
          Overview
        </Link>
        {!user?.marketing_agency_user && (
          <>
            <Link
              to={`/marketing-analytics/people?from=${dateFrom}&to=${dateTo}`}
              className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition inline-flex items-center gap-1"
            >
              <Users className="w-3 h-3" /> People
            </Link>
            <Link
              to={`/marketing-analytics/sales?from=${dateFrom}&to=${dateTo}`}
              className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition inline-flex items-center gap-1"
            >
              <TrendingUp className="w-3 h-3" /> Sales scoring
            </Link>
            <Link
              to={`/marketing-analytics/landings?from=${dateFrom}&to=${dateTo}`}
              className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition inline-flex items-center gap-1"
            >
              <Globe2 className="w-3 h-3" /> Landings &amp; Leads
            </Link>
            <Link
              to={`/marketing-analytics/chats?from=${dateFrom}&to=${dateTo}`}
              className="px-4 py-2 text-sm text-zinc-400 hover:text-zinc-200 border-b-2 border-transparent transition inline-flex items-center gap-1"
            >
              <MessagesSquare className="w-3 h-3" /> Chats
            </Link>
          </>
        )}
      </div>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-blue-400" />
            Marketing Analytics
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Meta Ads spend joined to CRM leads via <code className="text-blue-300">utm_campaign</code>.
          </p>
        </div>
        <Button
          onClick={fetchOverview}
          disabled={loading}
          className="bg-blue-600 hover:bg-blue-500"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Single period bar — preset chips on the left, exact date pickers on
          the right (they reflect the active preset and editing them flips
          monthAnchor → custom). No more duplicate date controls. */}
      <div className="flex items-center gap-2 flex-wrap text-xs bg-zinc-900/40 border border-zinc-800 rounded-lg px-3 py-2">
        <span className="text-zinc-500 uppercase tracking-wider">Period</span>
        {[
          { months: 0, label: "This month" },
          { months: 1, label: "Last month" },
        ].map((p) => {
          const r = calendarMonthRange(p.months);
          const active = monthAnchor === p.months;
          return (
            <button
              key={p.label}
              type="button"
              onClick={() => { setMonthAnchor(p.months); setDateFrom(r.from); setDateTo(r.to); }}
              className={`px-2.5 py-1 rounded border transition ${
                active
                  ? "bg-blue-500/20 text-blue-200 border-blue-500/40"
                  : "bg-zinc-900/60 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"
              }`}
              title={`${r.from} → ${r.to}`}
            >
              {p.label}
            </button>
          );
        })}
        <div className="inline-flex items-center bg-zinc-900/60 border border-zinc-800 rounded">
          <button
            type="button"
            onClick={() => {
              const next = (monthAnchor == null ? 1 : monthAnchor) + 1;
              const r = calendarMonthRange(next);
              setMonthAnchor(next);
              setDateFrom(r.from);
              setDateTo(r.to);
            }}
            className="px-2 py-1 text-zinc-400 hover:text-zinc-100 border-r border-zinc-800"
            title="Earlier month"
          >
            <ChevronRight className="w-3 h-3 rotate-180" />
          </button>
          <span className="px-2 py-1 text-zinc-300 min-w-[110px] text-center">
            {monthAnchor != null && monthAnchor >= 0
              ? calendarMonthRange(monthAnchor).label
              : "Custom range"}
          </span>
          <button
            type="button"
            onClick={() => {
              const next = Math.max(0, (monthAnchor == null ? 1 : monthAnchor) - 1);
              const r = calendarMonthRange(next);
              setMonthAnchor(next);
              setDateFrom(r.from);
              setDateTo(r.to);
            }}
            disabled={monthAnchor === 0}
            className="px-2 py-1 text-zinc-400 hover:text-zinc-100 disabled:opacity-30 border-l border-zinc-800"
            title="Later month"
          >
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
        <button
          type="button"
          onClick={() => {
            const r = allTimeRange();
            setMonthAnchor(-1);
            setDateFrom(r.from);
            setDateTo(r.to);
          }}
          className={`px-2.5 py-1 rounded border transition ${
            monthAnchor === -1
              ? "bg-blue-500/20 text-blue-200 border-blue-500/40"
              : "bg-zinc-900/60 text-zinc-400 border-zinc-800 hover:border-zinc-700 hover:text-zinc-200"
          }`}
        >
          All time
        </button>
        {/* Exact dates — inline, no labels. Editing flips to Custom. */}
        <div className="ml-auto flex items-center gap-1.5 text-zinc-400">
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setMonthAnchor(null); }}
            className="bg-zinc-900/60 border-zinc-800 text-xs h-7 w-36 text-zinc-200"
          />
          <span className="text-zinc-600">→</span>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setMonthAnchor(null); }}
            className="bg-zinc-900/60 border-zinc-800 text-xs h-7 w-36 text-zinc-200"
          />
          {monthAnchor === null && (
            <span className="px-1.5 py-0.5 rounded border bg-zinc-800/40 text-zinc-400 border-zinc-700 text-[10px] uppercase tracking-wider">
              Custom
            </span>
          )}
        </div>
      </div>

      {error && (
        <Card className="bg-red-900/20 border-red-800">
          <CardContent className="p-4 text-sm text-red-300">{error}</CardContent>
        </Card>
      )}

      {/* Meta sync freshness — explains rows where spend=$0 even though leads
          exist (campaign just launched, or sync is N hours stale). */}
      {data?.meta_sync && (
        <Card className="bg-zinc-900/40 border-zinc-800">
          <CardContent className="p-3 space-y-2 text-xs">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2 text-zinc-400">
                <Database className="w-4 h-4 text-blue-400" />
                <span>
                  Meta Ads spend last synced{" "}
                  <span className="text-zinc-200 font-medium">
                    {relTime(data.meta_sync.last_incremental_at)}
                  </span>
                  {data.meta_sync.last_incremental_at && (
                    <span className="text-zinc-600 ml-2">
                      ({new Date(data.meta_sync.last_incremental_at).toLocaleString()})
                    </span>
                  )}
                  {!data.meta_sync.last_incremental_at && (
                    <span className="text-amber-400 ml-2">— never synced</span>
                  )}
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={pullMetaNow}
                disabled={pullingMeta || loading}
                className="border-zinc-700 hover:bg-zinc-800 h-7"
              >
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${pullingMeta ? "animate-spin" : ""}`} />
                {pullingMeta ? "Pulling…" : "Pull Meta now"}
              </Button>
            </div>

            {/* Friendly per-error rows: account label, parsed error code,
                actionable hint. Replaces the previous raw-JSON tooltip. */}
            {data.meta_sync.errors?.length > 0 && (
              <div className="space-y-1.5">
                {data.meta_sync.errors.map((e, i) => {
                  const kind = e?.error_kind || "other";
                  const palette = {
                    token_revoked: { border: "border-red-500/40",   bg: "bg-red-500/10",   text: "text-red-300",   label: "Token revoked" },
                    permissions:   { border: "border-amber-500/40", bg: "bg-amber-500/10", text: "text-amber-300", label: "Permissions" },
                    rate_limited:  { border: "border-zinc-700",     bg: "bg-zinc-800/40",  text: "text-zinc-400",  label: "Rate-limited" },
                    bad_request:   { border: "border-red-500/40",   bg: "bg-red-500/10",   text: "text-red-300",   label: "Bad request" },
                    other:         { border: "border-amber-500/40", bg: "bg-amber-500/10", text: "text-amber-300", label: "Error" },
                  }[kind] || { border: "border-amber-500/40", bg: "bg-amber-500/10", text: "text-amber-300", label: "Error" };
                  return (
                    <div key={i} className={`border rounded px-2.5 py-1.5 ${palette.border} ${palette.bg}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <AlertTriangle className={`w-3.5 h-3.5 shrink-0 ${palette.text}`} />
                        <span className={`uppercase tracking-wider text-[10px] ${palette.text}`}>{palette.label}</span>
                        {e?.label && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-zinc-700 bg-zinc-900 text-zinc-300 text-[11px]">
                            {e.label}
                          </span>
                        )}
                        {e?.ad_account_id && (
                          <code className="text-zinc-500 text-[10px]">act_{e.ad_account_id}</code>
                        )}
                        {e?.step && (
                          <code className="text-[10px] px-1.5 py-0.5 rounded border border-zinc-700 bg-zinc-950 text-zinc-400" title="Failed sync step">
                            step: {e.step}
                          </code>
                        )}
                        <span className="text-zinc-400 text-[11px] truncate">{e?.detail || e?.error || ""}</span>
                      </div>
                      {e?.hint && (
                        <div className="text-zinc-300 text-[11px] mt-1 leading-snug">
                          {e.hint}
                          {kind === "permissions" && (
                            <>
                              {" "}
                              <Link to="/settings/integrations" className="text-blue-300 hover:underline whitespace-nowrap">
                                Open Integrations →
                              </Link>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Qualified leads by campaign — binary collapse of AI lead scoring.
          See Decision 2026-05-24 (qualified-binary-v1). The rich per-lead
          analysis (marketing.grade, sales.grade, scorecard, MEDDIC) still
          lives in the Lead Detail panel below; full manager scorecards on
          /marketing-analytics/people. */}
      {/* The AI analyze/re-analyze toolbar lived here. It is gone with the AI
          verdict: the qualified/trash counts come from the CRM's `Qualified`
          field alone, so grading leads with GPT no longer changes a single
          number on this page. Leaving buttons that spend money and move
          nothing is worse than leaving a gap. The AI coaching that DOES still
          drive something lives on /marketing-analytics/sales.
          Marker: crm-verdict-only-v1 */}

      {data?.campaigns?.length > 0 && (
        <CampaignsQualifiedSection
          campaigns={data.campaigns}
          owners={data.owners}
          dateFrom={dateFrom}
          dateTo={dateTo}
          leadsByCampaign={binaryLeadsByCampaign}
          loadingCampaigns={loadingBinaryCampaigns}
          onEnsureLeads={ensureBinaryCampaignLeads}
          onOpenLead={handleOpenLeadFromBinary}
        />
      )}
      {openLeadFlat && (
        <LeadDetailModal lead={openLeadFlat} onClose={() => setOpenLeadFlat(null)} />
      )}


      {/* Manager × Campaign matrix removed 2026-05-25 — duplicates the
          per-lead manager column inside CampaignsQualifiedSection expanded
          rows. Per-manager scorecards live on /marketing-analytics/people,
          per-lead coaching on /marketing-analytics/sales. */}

      {/* Daily spend + leads trend — dual bar: blue = spend, emerald = leads */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-50 text-base flex items-center gap-3 flex-wrap">
            <span>Daily spend & leads</span>
            <span className="text-xs text-zinc-500 font-normal flex items-center gap-3">
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded-sm bg-blue-500/70" /> spend ($)
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded-sm bg-emerald-500/70" /> leads (CRM)
              </span>
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dailyTrend.length === 0 ? (
            <div className="text-zinc-500 text-sm">No daily insights in range.</div>
          ) : (
            <div className="flex items-end gap-2 h-48 overflow-x-auto pb-2 px-1">
              {dailyTrend.map((r) => {
                const spend = Number(r.spend) || 0;
                const leads = Number(r.leads_crm) || 0;
                const hSpend = spend > 0 ? Math.max(2, (spend / dailyMax) * 130) : 0;
                const hLeads = leads > 0 ? Math.max(2, (leads / dailyLeadsMax) * 130) : 0;
                const spendLabel =
                  spend >= 1000
                    ? `$${(spend / 1000).toFixed(1)}k`
                    : spend >= 1
                    ? `$${Math.round(spend)}`
                    : "—";
                const leadsLabel = leads > 0 ? `${leads}` : "—";
                return (
                  <div
                    key={r.date}
                    className="flex flex-col items-center gap-1 shrink-0 group"
                    title={`${r.date} · spend ${fmtUsd(spend)} · ${leads} leads landed (CRM) · ${fmtInt(r.leads_meta)} leads (Meta pixel) · ${fmtInt(r.clicks)} clicks · ${fmtInt(r.impressions)} impressions`}
                  >
                    <div className="flex flex-col items-center leading-none">
                      <div className="text-[10px] text-emerald-300 font-mono group-hover:text-emerald-200 whitespace-nowrap">
                        {leadsLabel}
                      </div>
                      <div className="text-[10px] text-zinc-300 font-mono group-hover:text-blue-300 whitespace-nowrap">
                        {spendLabel}
                      </div>
                    </div>
                    <div className="flex items-end gap-0.5">
                      <div
                        className="w-3 bg-blue-500/60 group-hover:bg-blue-400 rounded-t transition-colors"
                        style={{ height: `${hSpend}px` }}
                        title="spend"
                      />
                      <div
                        className="w-3 bg-emerald-500/60 group-hover:bg-emerald-400 rounded-t transition-colors"
                        style={{ height: `${hLeads}px` }}
                        title="leads"
                      />
                    </div>
                    <div className="text-[10px] text-zinc-500 whitespace-nowrap">{r.date.slice(5)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-xs text-zinc-500 text-center pt-4 border-t border-zinc-800 space-y-1">
        <div>Date range: {data?.date_from} → {data?.date_to}</div>
        {/* The cascade from "rows in the CRM" to "leads we report". It is shown
            here, next to the number itself, because the count used to be
            explainable only on a separate Diagnostic screen — and the two
            screens disagreed. Both now read the same object from the backend,
            so this line cannot drift from the KPI above it. */}
        {data?.kpis?.lead_funnel?.contacts_in_range > 0 && (
          <div className="text-zinc-500">
            {data.kpis.lead_funnel.contacts_in_range} contact
            {data.kpis.lead_funnel.contacts_in_range === 1 ? "" : "s"} created in the CRM in this range
            {data.kpis.lead_funnel.excluded_test > 0 && (
              <> · −{data.kpis.lead_funnel.excluded_test} our own test submission
                {data.kpis.lead_funnel.excluded_test === 1 ? "" : "s"}</>
            )}
            {data.kpis.lead_funnel.excluded_not_marketing > 0 && (
              <> · −{data.kpis.lead_funnel.excluded_not_marketing} with no marketing signal
                (manual / offline entries)</>
            )}
            {" "}= <span className="text-zinc-300">{data.kpis.lead_funnel.counted_as_leads} leads</span> counted here.
          </div>
        )}
        {data?.kpis?.untagged_leads_crm > 0 && (
          <div className="text-amber-400">
            {data.kpis.untagged_leads_crm} marketing lead{data.kpis.untagged_leads_crm === 1 ? "" : "s"} {" "}
            without <code>utm_campaign</code> — fix Meta URL parameters template on TR campaigns.
          </div>
        )}
      </div>
    </div>
    </TooltipProvider>
  );
}
